import { ImmutableMap, Prop, QueryParams, RequiredOnly, isObject, isString } from './utility.model';

export const UNIT_TYPE = {
  indoorUnit: 'Indoor unit',
  outdoorUnit: 'Outdoor unit',
  waterTank: 'Water tank',
  boosterHeater: 'Booster heater',
  backupHeater: 'Backup heater',
  controller: 'Controller',
  fanCoilUnit: 'Fan coil unit',
  radiator: 'Radiator',
  boiler: 'Boiler',
  other: 'Other',
} as const;
export type UnitType = (typeof UNIT_TYPE)[keyof typeof UNIT_TYPE];

type ModelRequiredProps = {
  productName: string;
  pillar: string;
  productRange: string;
  unitType: UnitType;
  capacityClass: string;
};

type ModelOptionalProps = {
  mountType?: string;
  monoblocLarge?: string;
  monoblocLargeWithoutBuh?: string;
  ukModel?: string;
  heatingOnly?: string;
  coolingOnly?: string;
  hpClass?: string;
  refrigeratingTon?: string;
  coolingCapacityMax?: string;
  coolingCapacityMin?: string;
  heatingCapacityMax?: string;
  heatingCapacityMin?: string;
  eerMax?: string;
  eerMin?: string;
  copMax?: string;
  copMin?: string;
  seerMax?: string;
  seerMin?: string;
  scopMax?: string;
  scopMin?: string;
  soundMax?: string;
  leftMin?: string;
  rightMin?: string;
  topMin?: string;
  bottomMin?: string;
  frontMin?: string;
  backMin?: string;
  btwOpenMin?: string;
  btwUnitMin?: string;
  clOutdoorMax?: string;
  clOutdoorMin?: string;
  htOutdoorMax?: string;
  htOutdoorMin?: string;
  clIndoorMax?: string;
  clIndoorMin?: string;
  htIndoorMax?: string;
  htIndoorMin?: string;
  humidityMax?: string;
  refType?: string;
  refGwp?: string;
  refChargeNom?: string;
  refChargeMax?: string;
  refChargeMin?: string;
  refAdditionalMax?: string;
  refAdditionalMin?: string;
  refCircuits?: string;
  refLiquidDiameter?: string;
  refGasDiameter?: string;
  refHpLpDiameter?: string;
  refLengthMax?: string;
  refLengthMin?: string;
  refHeightMax?: string;
  refHpMax?: string;
  refHpMin?: string;
  refLpMax?: string;
  refLpMin?: string;
  refHpLpMax?: string;
  refHpLpMin?: string;
  heatingMin?: string;
  coolingMin?: string;
  ewMin?: string;
  cwMin?: string;
  heatingMaxLm?: string;
  heatingMinLm?: string;
  coolingMaxLm?: string;
  coolingMinLm?: string;
  ewMaxLm?: string;
  ewMinLm?: string;
  cwMaxLm?: string;
  cwMinLm?: string;
  htWaterPreMax?: string;
  htWaterPreMin?: string;
  dhWaterPreMax?: string;
  dhWaterPreMin?: string;
  ewWaterPreMax?: string;
  ewWaterPreMin?: string;
  cwWaterPreMax?: string;
  cwWaterPreMin?: string;
  htWaterTempMax?: string;
  htWaterTempMin?: string;
  dhWaterTempMax?: string;
  dhWaterTempMin?: string;
  ewWaterTempMax?: string;
  ewWaterTempMin?: string;
  cwWaterTempMax?: string;
  cwWaterTempMin?: string;
  glycolMax?: string;
  glycolMin?: string;
  glycolMaxDegC?: string;
  glycolMinDegC?: string;
  iuOuWaterDiameter?: string;
  shDiameter?: string;
  wsDiameter?: string;
  geoDiameter?: string;
  condWaterDiameter?: string;
  evapWaterDiameter?: string;
  drainDiameter?: string;
  drainHeightMax?: string;
  supplyWaterDiameter?: string;
  magneticFilterDirtSeparator?: string;
  autoAirpurge?: string;
  manualAirpurge?: string;
  unitPowerMax?: string;
  unitPowerMin?: string;
  unitCurrentMax?: string;
  unitFuseRate?: string;
  unitPhase?: string;
  unitFrequency?: string;
  unitPowerCableDiameter?: string;
  iuOuCableDiameter?: string;
  unitPowerCableArea?: string;
  iuOuCableArea?: string;
  iuOuCableLengthMax?: string;
  compType?: string;
  compModel1?: string;
  compModel2?: string;
  compQty?: string;
  compMax?: string;
  compMin?: string;
  comp1Max?: string;
  comp1Min?: string;
  compMaxA?: string;
  comp2Min?: string;
  compStepsMax?: string;
  compStepsMin?: string;
  compMaxHz?: string;
  compMinHz?: string;
  compHeaterMax?: string;
  compHeaterMin?: string;
  compHeaterMaxA?: string;
  compHeaterMinA?: string;
  compHeaterOn?: string;
  compHeaterOff?: string;
  compHeaterMaxKOhm?: string;
  compHeaterMinKOhm?: string;
  compDischargeMax?: string;
  compDischargeMin?: string;
  compSuctionMax?: string;
  compSuctionMin?: string;
  compBodyMax?: string;
  compBodyMin?: string;
  compDshMax?: string;
  compSshMin?: string;
  compSshMax?: string;
  compDshMin?: string;
  compUVMax?: string;
  compUVMin?: string;
  compVWMax?: string;
  compVWMin?: string;
  compUWMax?: string;
  compUWMin?: string;
  fanDc?: string;
  fanQty?: string;
  fanAirFlowMax?: string;
  fanAirFlowMin?: string;
  fanMax?: string;
  fanMin?: string;
  fanMaxA?: string;
  fanMinA?: string;
  fanMaxOhm?: string;
  fanMinOhm?: string;
  fanControlMax?: string;
  fanControlMin?: string;
  fanRotationMax?: string;
  fanRotationMin?: string;
  fanStepsMax?: string;
  fanStepsMin?: string;
  fanVfd?: string;
  fanMaxHz?: string;
  fanMinHz?: string;
  condWaterCooled?: string;
  condPressurDrop?: string;
  condApproachMax?: string;
  condApproachMin?: string;
  condScMax?: string;
  condScMin?: string;
  evapPressurDrop?: string;
  evapApproachMax?: string;
  evapApproachMin?: string;
  gearEexv?: string;
  exvLiquidInMax?: string;
  exvLiquidInMin?: string;
  exvLiquidOutMax?: string;
  exvLiquidOutMin?: string;
  eexvMainMax?: string;
  eexvMainMin?: string;
  eexvInjeMax?: string;
  eexvInjeMin?: string;
  eexvPulseMax?: string;
  eexvPulseMin?: string;
  eexvMax?: string;
  eexvMin?: string;
  ecoLiquidInMax?: string;
  ecoLiquidInMin?: string;
  ecoLiquidOutMax?: string;
  ecoLiquidOutMin?: string;
  ecoInjeInMax?: string;
  ecoInjeInMin?: string;
  ecoInjeOutMax?: string;
  ecoInjeOutMin?: string;
  contType?: string;
  contVoltageMax?: string;
  contVoltageMin?: string;
  typeElConn?: string;
  contCableType?: string;
  contCableDiameter?: string;
  contCableLengthMax?: string;
  mainPumpMax?: string;
  mainPumpMin?: string;
  mainPumpMaxA?: string;
  mainPumpMinA?: string;
  mainPumpPwmMax?: string;
  mainPumpPwmMin?: string;
  mainPumpMaxHz?: string;
  mainPumpMinHz?: string;
  bizonePumpMax?: string;
  bizonePumpMin?: string;
  bizonePumpMaxA?: string;
  bizonePumpMinA?: string;
  bizonePumpPwmMax?: string;
  bizonePumpPwmMin?: string;
  bizonePumpMaxHz?: string;
  bizonePumpMinHz?: string;
  threeWvVoltageMax?: string;
  threeWvVoltageMin?: string;
  threeWvMax?: string;
  threeWvMin?: string;
  threeWvBizone?: string;
  threeWvBizoneMax?: string;
  threeWvBizoneMin?: string;
  threeWvBizoneMaxKOhm?: string;
  threeWvBizoneMinKOhm?: string;
  threeWvGeneratorMax?: string;
  threeWvGeneratorMin?: string;
  threeWvGeneratorMaxKOhm?: string;
  threeWvGeneratorMinKOhm?: string;
  main4wvMax?: string;
  main4wvMin?: string;
  main4wvMaxKOhm?: string;
  main4wvMinKOhm?: string;
  sub4wvMax?: string;
  sub4wvMin?: string;
  sub4wvMaxKOhm?: string;
  sub4wvMinKOhm?: string;
  mainPcbPowerMax?: string;
  mainPcbPowerMin?: string;
  mainPcbFanMax?: string;
  mainPcbFanMin?: string;
  mainPcbRectifierMax?: string;
  mainPcbRectifierMin?: string;
  mainPcbV1rMax?: string;
  mainPcbV1rMin?: string;
  mainPcbV2rMax?: string;
  mainPcbV2rMin?: string;
  mainPcbV3rMax?: string;
  mainPcbV3rMin?: string;
  mainPcbV4rMax?: string;
  mainPcbV4rMin?: string;
  noisePcbPowerMax?: string;
  noisePcbPowerMin?: string;
  noisePcbLaMax?: string;
  noisePcbLaMin?: string;
  noisePcbLbMax?: string;
  noisePcbLbMin?: string;
  acspcbMax?: string;
  acspcbMin?: string;
  bizonePcbMax?: string;
  bizonePcbMin?: string;
  currentLoopPcbMax?: string;
  currentLoopPcbMin?: string;
  flashPcbMax?: string;
  flashPcbMin?: string;
  hydroPcbMax?: string;
  hydroPcbMin?: string;
  hydroExtPcbMax?: string;
  hydroExtPcbMin?: string;
  invpcbLaMax?: string;
  invpcbLaMin?: string;
  invpcbLbMax?: string;
  invpcbLbMin?: string;
  interfacePcbMax?: string;
  interfacePcbMin?: string;
  leakPcbMax?: string;
  leakPcbMin?: string;
  leakPcbMaxOhm?: string;
  leakPcbMinOhm?: string;
  bhupcbControlMax?: string;
  bhupcbControlMin?: string;
  bhupcbPowerMax?: string;
  bhupcbPowerMin?: string;
  powerSupplyPcbMax?: string;
  powerSupplyPcbMin?: string;
  powerSupplyPcbPcbMax?: string;
  powerSupplyPcbPcbMin?: string;
  reactorMax?: string;
  reactorMin?: string;
  buhType?: string;
  buh3v?: string;
  buh3vFuseRate?: string;
  buh3vMax?: string;
  buh3vMin?: string;
  buh6v1n?: string;
  buh6v1nFuseRate?: string;
  buh6v1nMax?: string;
  buh6v1nMin?: string;
  buh6v3n?: string;
  buh6v3nFuseRate?: string;
  buh6v3nMax?: string;
  buh6v3nMin?: string;
  buh9w?: string;
  buh9wFuseRate?: string;
  buh9wMax?: string;
  buh9wMin?: string;
  bshMax?: string;
  bshMin?: string;
  bshMaxOhm?: string;
  bshMinOhm?: string;
  reliefValve?: string;
  reliefValveDegC?: string;
  antiFreezeValve?: string;
  buhThermalProtector?: string;
  bshThermalProtector?: string;
  hps1OperatingMax?: string;
  hps1OperatingMin?: string;
  hps1ResetMax?: string;
  hps1ResetMin?: string;
  hps1VoltageMax?: string;
  hps1VoltageMin?: string;
  hps2OperatingMax?: string;
  hps2OperatingMin?: string;
  hps2ResetMax?: string;
  hps2ResetMin?: string;
  hps2VoltageMax?: string;
  hps2VoltageMin?: string;
  lps1OperatingMax?: string;
  lps1OperatingMin?: string;
  lps1ResetMax?: string;
  lps1ResetMin?: string;
  lps1VoltageMax?: string;
  lps1VoltageMin?: string;
  lps2OperatingMax?: string;
  lps2OperatingMin?: string;
  lps2ResetMax?: string;
  lps2ResetMin?: string;
  lps2VoltageMax?: string;
  lps2VoltageMin?: string;
  lps2ConnectedTo?: string;
  lps2IntermediateConnect?: string;
  hpSensorMax?: string;
  hpSensorMin?: string;
  hpSensorReadMax?: string;
  hpSensorReadMin?: string;
  lpSensorMax?: string;
  lpSensorMin?: string;
  lpSensorReadMax?: string;
  lpSensorReadMin?: string;
  wpSensorMax?: string;
  wpSensorMin?: string;
  wpSensorReadMax?: string;
  wpSensorReadMin?: string;
  svMax?: string;
  svMin?: string;
  svMaxKOhm?: string;
  svMinKOhm?: string;
  refThermistorAMax?: string;
  refThermistorAMin?: string;
  refThermistorBMax?: string;
  refThermistorBMin?: string;
  refThermistorCMax?: string;
  refThermistorCMin?: string;
  waterThermistorAMax?: string;
  waterThermistorAMin?: string;
  waterThermistorBMax?: string;
  waterThermistorBMin?: string;
  waterThermistorCMax?: string;
  waterThermistorCMin?: string;
  flowSensorMax?: string;
  flowSensorMin?: string;
  flowSensorMaxHz?: string;
  flowSensorMinHz?: string;
  gasSensorIuMax?: string;
  gasSensorIuMix?: string;
  gasSensorIuMaxPct?: string;
  gasSensorIuMixPct?: string;
  gasSensorOuMax?: string;
  gasSensorOuMix?: string;
  gasSensorOuMaxPct?: string;
  gasSensorOuMixPct?: string;
  refrigerantRectifier?: string;
  refrigerantRectifierMax?: string;
  refrigerantRectifierMin?: string;
  ekhvconvReversibleKitInstalled?: string;
  boilerDaikin?: string;
  boilerVentDiameter?: string;
  boilerFlueDiameter?: string;
  boilerVentLengthMax?: string;
  boilerFlueLengthMax?: string;
  boilerFlueVentMin?: string;
  boilerGasPressier?: string;
  boilerCo2HMax?: string;
  boilerCo2HMin?: string;
  boilerCo2LMax?: string;
  boilerCo2LMin?: string;
  boilerMax?: string;
  boilerMin?: string;
};

type ModelProvidedProps = {
  mgmtDeptId: string;
  modelId: string;
  updatedAt: number;
  version: number;
};

export type ModelQueryParams = QueryParams;

type ModelCreateParams = ModelRequiredProps & Partial<ModelOptionalProps>;
export type ModelsCreateParams = {
  items: ModelCreateParams[];
};

export type Model = ModelRequiredProps & Partial<ModelOptionalProps> & ModelProvidedProps;

type ModelsExtractRequiredParams = {
  productName: string;
  limit: number;
  unitType: UnitType;
};

type ModelsExtractProvidedParams = {
  modelId: string;
};

export type ModelsExtractParams = QueryParams & ModelsExtractRequiredParams;

export type ModelsExtract = Omit<ModelsExtractParams, 'limit'> & ModelsExtractProvidedParams;

export function isModelsCreateParams(value: unknown): value is ModelsCreateParams {
  return isObject(value) && Array.isArray(value['items']) && value['items'].every(val => isModelCreateParams(val));
}

function isModelCreateParams(value: unknown): value is ModelCreateParams {
  const sProps: Prop<RequiredOnly<ModelCreateParams>, string>[] = ['productName', 'pillar', 'productRange', 'unitType', 'capacityClass'];
  return isObject(value) && sProps.every(prop => isString(value[prop]));
}

export class Models extends ImmutableMap<string, Model> {
  constructor(models?: Model[] | Iterable<Model>) {
    super((model: Model) => model.modelId, models);
  }

  protected clone(models?: Model[] | Iterable<Model>): this {
    return new Models(models) as this;
  }
}

export type CsvRowFormat = {
  productName: string;
  pillar: string;
  productRange: string;
  unitType: string;
  mountType?: string;
  monoblocLarge?: string;
  monoblocLargeWithoutBuh?: string;
  ukModel?: string;
  capacityClass: string;
  heatingOnly?: string;
  coolingOnly?: string;
  hpClass?: string;
  refrigeratingTon?: string;
  coolingCapacityMax?: string;
  coolingCapacityMin?: string;
  heatingCapacityMax?: string;
  heatingCapacityMin?: string;
  eerMax?: string;
  eerMin?: string;
  copMax?: string;
  copMin?: string;
  seerMax?: string;
  seerMin?: string;
  scopMax?: string;
  scopMin?: string;
  soundMax?: string;
  leftMin?: string;
  rightMin?: string;
  topMin?: string;
  bottomMin?: string;
  frontMin?: string;
  backMin?: string;
  btwOpenMin?: string;
  btwUnitMin?: string;
  clOutdoorMax?: string;
  clOutdoorMin?: string;
  htOutdoorMax?: string;
  htOutdoorMin?: string;
  clIndoorMax?: string;
  clIndoorMin?: string;
  htIndoorMax?: string;
  htIndoorMin?: string;
  humidityMax?: string;
  refType: string;
  refGwp?: string;
  refChargeNom?: string;
  refChargeMax?: string;
  refChargeMin?: string;
  refAdditionalMax?: string;
  refAdditionalMin?: string;
  refCircuits?: string;
  refLiquidDiameter?: string;
  refGasDiameter?: string;
  refHpLpDiameter?: string;
  refLengthMax?: string;
  refLengthMin?: string;
  refHeightMax?: string;
  refHpMax?: string;
  refHpMin?: string;
  refLpMax?: string;
  refLpMin?: string;
  refHpLpMax?: string;
  refHpLpMin?: string;
  heatingMin?: string;
  coolingMin?: string;
  ewMin?: string;
  cwMin?: string;
  heatingMaxLm?: string;
  heatingMinLm?: string;
  coolingMaxLm?: string;
  coolingMinLm?: string;
  ewMaxLm?: string;
  ewMinLm?: string;
  cwMaxLm?: string;
  cwMinLm?: string;
  htWaterPreMax?: string;
  htWaterPreMin?: string;
  dhWaterPreMax?: string;
  dhWaterPreMin?: string;
  ewWaterPreMax?: string;
  ewWaterPreMin?: string;
  cwWaterPreMax?: string;
  cwWaterPreMin?: string;
  htWaterTempMax?: string;
  htWaterTempMin?: string;
  dhWaterTempMax?: string;
  dhWaterTempMin?: string;
  ewWaterTempMax?: string;
  ewWaterTempMin?: string;
  cwWaterTempMax?: string;
  cwWaterTempMin?: string;
  glycolMax?: string;
  glycolMin?: string;
  glycolMaxDegC?: string;
  glycolMinDegC?: string;
  iuOuWaterDiameter?: string;
  shDiameter?: string;
  wsDiameter?: string;
  geoDiameter?: string;
  condWaterDiameter?: string;
  evapWaterDiameter?: string;
  drainDiameter?: string;
  drainHeightMax?: string;
  supplyWaterDiameter?: string;
  magneticFilterDirtSeparator?: string;
  autoAirpurge?: string;
  manualAirpurge?: string;
  unitPowerMax?: string;
  unitPowerMin?: string;
  unitCurrentMax?: string;
  unitFuseRate?: string;
  unitPhase?: string;
  unitFrequency?: string;
  unitPowerCableDiameter?: string;
  iuOuCableDiameter?: string;
  unitPowerCableArea?: string;
  iuOuCableArea?: string;
  iuOuCableLengthMax?: string;
  compType?: string;
  compModel1?: string;
  compModel2?: string;
  compQty?: string;
  compMax?: string;
  compMin?: string;
  comp1Max?: string;
  comp1Min?: string;
  compMaxA?: string;
  comp2Min?: string;
  compStepsMax?: string;
  compStepsMin?: string;
  compMaxHz?: string;
  compMinHz?: string;
  compHeaterMax?: string;
  compHeaterMin?: string;
  compHeaterMaxA?: string;
  compHeaterMinA?: string;
  compHeaterOn?: string;
  compHeaterOff?: string;
  compHeaterMaxKOhm?: string;
  compHeaterMinKOhm?: string;
  compDischargeMax?: string;
  compDischargeMin?: string;
  compSuctionMax?: string;
  compSuctionMin?: string;
  compBodyMax?: string;
  compBodyMin?: string;
  compDshMax?: string;
  compSshMin?: string;
  compSshMax?: string;
  compDshMin?: string;
  compUVMax?: string;
  compUVMin?: string;
  compVWMax?: string;
  compVWMin?: string;
  compUWMax?: string;
  compUWMin?: string;
  fanDc?: string;
  fanQty?: string;
  fanAirFlowMax?: string;
  fanAirFlowMin?: string;
  fanMax?: string;
  fanMin?: string;
  fanMaxA?: string;
  fanMinA?: string;
  fanMaxOhm?: string;
  fanMinOhm?: string;
  fanControlMax?: string;
  fanControlMin?: string;
  fanRotationMax?: string;
  fanRotationMin?: string;
  fanStepsMax?: string;
  fanStepsMin?: string;
  fanVfd?: string;
  fanMaxHz?: string;
  fanMinHz?: string;
  condWaterCooled?: string;
  condPressurDrop?: string;
  condApproachMax?: string;
  condApproachMin?: string;
  condScMax?: string;
  condScMin?: string;
  evapPressurDrop?: string;
  evapApproachMax?: string;
  evapApproachMin?: string;
  gearEexv?: string;
  exvLiquidInMax?: string;
  exvLiquidInMin?: string;
  exvLiquidOutMax?: string;
  exvLiquidOutMin?: string;
  eexvMainMax?: string;
  eexvMainMin?: string;
  eexvInjeMax?: string;
  eexvInjeMin?: string;
  eexvPulseMax?: string;
  eexvPulseMin?: string;
  eexvMax?: string;
  eexvMin?: string;
  ecoLiquidInMax?: string;
  ecoLiquidInMin?: string;
  ecoLiquidOutMax?: string;
  ecoLiquidOutMin?: string;
  ecoInjeInMax?: string;
  ecoInjeInMin?: string;
  ecoInjeOutMax?: string;
  ecoInjeOutMin?: string;
  contType?: string;
  contVoltageMax?: string;
  contVoltageMin?: string;
  typeElConn?: string;
  contCableType?: string;
  contCableDiameter?: string;
  contCableLengthMax?: string;
  mainPumpMax?: string;
  mainPumpMin?: string;
  mainPumpMaxA?: string;
  mainPumpMinA?: string;
  mainPumpPwmMax?: string;
  mainPumpPwmMin?: string;
  mainPumpMaxHz?: string;
  mainPumpMinHz?: string;
  bizonePumpMax?: string;
  bizonePumpMin?: string;
  bizonePumpMaxA?: string;
  bizonePumpMinA?: string;
  bizonePumpPwmMax?: string;
  bizonePumpPwmMin?: string;
  bizonePumpMaxHz?: string;
  bizonePumpMinHz?: string;
  threeWvVoltageMax?: string;
  threeWvVoltageMin?: string;
  threeWvMax?: string;
  threeWvMin?: string;
  threeWvBizone?: string;
  threeWvBizoneMax?: string;
  threeWvBizoneMin?: string;
  threeWvBizoneMaxKOhm?: string;
  threeWvBizoneMinKOhm?: string;
  threeWvGeneratorMax?: string;
  threeWvGeneratorMin?: string;
  threeWvGeneratorMaxKOhm?: string;
  threeWvGeneratorMinKOhm?: string;
  main4wvMax?: string;
  main4wvMin?: string;
  main4wvMaxKOhm?: string;
  main4wvMinKOhm?: string;
  sub4wvMax?: string;
  sub4wvMin?: string;
  sub4wvMaxKOhm?: string;
  sub4wvMinKOhm?: string;
  mainPcbPowerMax?: string;
  mainPcbPowerMin?: string;
  mainPcbFanMax?: string;
  mainPcbFanMin?: string;
  mainPcbRectifierMax?: string;
  mainPcbRectifierMin?: string;
  mainPcbV1rMax?: string;
  mainPcbV1rMin?: string;
  mainPcbV2rMax?: string;
  mainPcbV2rMin?: string;
  mainPcbV3rMax?: string;
  mainPcbV3rMin?: string;
  mainPcbV4rMax?: string;
  mainPcbV4rMin?: string;
  noisePcbPowerMax?: string;
  noisePcbPowerMin?: string;
  noisePcbLaMax?: string;
  noisePcbLaMin?: string;
  noisePcbLbMax?: string;
  noisePcbLbMin?: string;
  acspcbMax?: string;
  acspcbMin?: string;
  bizonePcbMax?: string;
  bizonePcbMin?: string;
  currentLoopPcbMax?: string;
  currentLoopPcbMin?: string;
  flashPcbMax?: string;
  flashPcbMin?: string;
  hydroPcbMax?: string;
  hydroPcbMin?: string;
  hydroExtPcbMax?: string;
  hydroExtPcbMin?: string;
  invpcbLaMax?: string;
  invpcbLaMin?: string;
  invpcbLbMax?: string;
  invpcbLbMin?: string;
  interfacePcbMax?: string;
  interfacePcbMin?: string;
  leakPcbMax?: string;
  leakPcbMin?: string;
  leakPcbMaxOhm?: string;
  leakPcbMinOhm?: string;
  bhupcbControlMax?: string;
  bhupcbControlMin?: string;
  bhupcbPowerMax?: string;
  bhupcbPowerMin?: string;
  powerSupplyPcbMax?: string;
  powerSupplyPcbMin?: string;
  powerSupplyPcbPcbMax?: string;
  powerSupplyPcbPcbMin?: string;
  reactorMax?: string;
  reactorMin?: string;
  buhType?: string;
  buh3v?: string;
  buh3vFuseRate?: string;
  buh3vMax?: string;
  buh3vMin?: string;
  buh6v1n?: string;
  buh6v1nFuseRate?: string;
  buh6v1nMax?: string;
  buh6v1nMin?: string;
  buh6v3n?: string;
  buh6v3nFuseRate?: string;
  buh6v3nMax?: string;
  buh6v3nMin?: string;
  buh9w?: string;
  buh9wFuseRate?: string;
  buh9wMax?: string;
  buh9wMin?: string;
  bshMax?: string;
  bshMin?: string;
  bshMaxOhm?: string;
  bshMinOhm?: string;
  reliefValve?: string;
  reliefValveDegC?: string;
  antiFreezeValve?: string;
  buhThermalProtector?: string;
  bshThermalProtector?: string;
  hps1OperatingMax?: string;
  hps1OperatingMin?: string;
  hps1ResetMax?: string;
  hps1ResetMin?: string;
  hps1VoltageMax?: string;
  hps1VoltageMin?: string;
  hps2OperatingMax?: string;
  hps2OperatingMin?: string;
  hps2ResetMax?: string;
  hps2ResetMin?: string;
  hps2VoltageMax?: string;
  hps2VoltageMin?: string;
  lps1OperatingMax?: string;
  lps1OperatingMin?: string;
  lps1ResetMax?: string;
  lps1ResetMin?: string;
  lps1VoltageMax?: string;
  lps1VoltageMin?: string;
  lps2OperatingMax?: string;
  lps2OperatingMin?: string;
  lps2ResetMax?: string;
  lps2ResetMin?: string;
  lps2VoltageMax?: string;
  lps2VoltageMin?: string;
  lps2ConnectedTo?: string;
  lps2IntermediateConnect?: string;
  hpSensorMax?: string;
  hpSensorMin?: string;
  hpSensorReadMax?: string;
  hpSensorReadMin?: string;
  lpSensorMax?: string;
  lpSensorMin?: string;
  lpSensorReadMax?: string;
  lpSensorReadMin?: string;
  wpSensorMax?: string;
  wpSensorMin?: string;
  wpSensorReadMax?: string;
  wpSensorReadMin?: string;
  svMax?: string;
  svMin?: string;
  svMaxKOhm?: string;
  svMinKOhm?: string;
  refThermistorAMax?: string;
  refThermistorAMin?: string;
  refThermistorBMax?: string;
  refThermistorBMin?: string;
  refThermistorCMax?: string;
  refThermistorCMin?: string;
  waterThermistorAMax?: string;
  waterThermistorAMin?: string;
  waterThermistorBMax?: string;
  waterThermistorBMin?: string;
  waterThermistorCMax?: string;
  waterThermistorCMin?: string;
  flowSensorMax?: string;
  flowSensorMin?: string;
  flowSensorMaxHz?: string;
  flowSensorMinHz?: string;
  gasSensorIuMax?: string;
  gasSensorIuMix?: string;
  gasSensorIuMaxPct?: string;
  gasSensorIuMixPct?: string;
  gasSensorOuMax?: string;
  gasSensorOuMix?: string;
  gasSensorOuMaxPct?: string;
  gasSensorOuMixPct?: string;
  refrigerantRectifier?: string;
  refrigerantRectifierMax?: string;
  refrigerantRectifierMin?: string;
  ekhvconvReversibleKitInstalled?: string;
  boilerDaikin?: string;
  boilerVentDiameter?: string;
  boilerFlueDiameter?: string;
  boilerVentLengthMax?: string;
  boilerFlueLengthMax?: string;
  boilerFlueVentMin?: string;
  boilerGasPressier?: string;
  boilerCo2HMax?: string;
  boilerCo2HMin?: string;
  boilerCo2LMax?: string;
  boilerCo2LMin?: string;
  boilerMax?: string;
  boilerMin?: string;
};

export const CSV_ROW_FORMAT_PROPS: (keyof CsvRowFormat)[] = [
  'productName',
  'pillar',
  'productRange',
  'unitType',
  'mountType',
  'monoblocLarge',
  'monoblocLargeWithoutBuh',
  'ukModel',
  'capacityClass',
  'heatingOnly',
  'coolingOnly',
  'hpClass',
  'refrigeratingTon',
  'coolingCapacityMax',
  'coolingCapacityMin',
  'heatingCapacityMax',
  'heatingCapacityMin',
  'eerMax',
  'eerMin',
  'copMax',
  'copMin',
  'seerMax',
  'seerMin',
  'scopMax',
  'scopMin',
  'soundMax',
  'leftMin',
  'rightMin',
  'topMin',
  'bottomMin',
  'frontMin',
  'backMin',
  'btwOpenMin',
  'btwUnitMin',
  'clOutdoorMax',
  'clOutdoorMin',
  'htOutdoorMax',
  'htOutdoorMin',
  'clIndoorMax',
  'clIndoorMin',
  'htIndoorMax',
  'htIndoorMin',
  'humidityMax',
  'refType',
  'refGwp',
  'refChargeNom',
  'refChargeMax',
  'refChargeMin',
  'refAdditionalMax',
  'refAdditionalMin',
  'refCircuits',
  'refLiquidDiameter',
  'refGasDiameter',
  'refHpLpDiameter',
  'refLengthMax',
  'refLengthMin',
  'refHeightMax',
  'refHpMax',
  'refHpMin',
  'refLpMax',
  'refLpMin',
  'refHpLpMax',
  'refHpLpMin',
  'heatingMin',
  'coolingMin',
  'ewMin',
  'cwMin',
  'heatingMaxLm',
  'heatingMinLm',
  'coolingMaxLm',
  'coolingMinLm',
  'ewMaxLm',
  'ewMinLm',
  'cwMaxLm',
  'cwMinLm',
  'htWaterPreMax',
  'htWaterPreMin',
  'dhWaterPreMax',
  'dhWaterPreMin',
  'ewWaterPreMax',
  'ewWaterPreMin',
  'cwWaterPreMax',
  'cwWaterPreMin',
  'htWaterTempMax',
  'htWaterTempMin',
  'dhWaterTempMax',
  'dhWaterTempMin',
  'ewWaterTempMax',
  'ewWaterTempMin',
  'cwWaterTempMax',
  'cwWaterTempMin',
  'glycolMax',
  'glycolMin',
  'glycolMaxDegC',
  'glycolMinDegC',
  'iuOuWaterDiameter',
  'shDiameter',
  'wsDiameter',
  'geoDiameter',
  'condWaterDiameter',
  'evapWaterDiameter',
  'drainDiameter',
  'drainHeightMax',
  'supplyWaterDiameter',
  'magneticFilterDirtSeparator',
  'autoAirpurge',
  'manualAirpurge',
  'unitPowerMax',
  'unitPowerMin',
  'unitCurrentMax',
  'unitFuseRate',
  'unitPhase',
  'unitFrequency',
  'unitPowerCableDiameter',
  'iuOuCableDiameter',
  'unitPowerCableArea',
  'iuOuCableArea',
  'iuOuCableLengthMax',
  'compType',
  'compModel1',
  'compModel2',
  'compQty',
  'compMax',
  'compMin',
  'comp1Max',
  'comp1Min',
  'compMaxA',
  'comp2Min',
  'compStepsMax',
  'compStepsMin',
  'compMaxHz',
  'compMinHz',
  'compHeaterMax',
  'compHeaterMin',
  'compHeaterMaxA',
  'compHeaterMinA',
  'compHeaterOn',
  'compHeaterOff',
  'compHeaterMaxKOhm',
  'compHeaterMinKOhm',
  'compDischargeMax',
  'compDischargeMin',
  'compSuctionMax',
  'compSuctionMin',
  'compBodyMax',
  'compBodyMin',
  'compDshMax',
  'compSshMin',
  'compSshMax',
  'compDshMin',
  'compUVMax',
  'compUVMin',
  'compVWMax',
  'compVWMin',
  'compUWMax',
  'compUWMin',
  'fanDc',
  'fanQty',
  'fanAirFlowMax',
  'fanAirFlowMin',
  'fanMax',
  'fanMin',
  'fanMaxA',
  'fanMinA',
  'fanMaxOhm',
  'fanMinOhm',
  'fanControlMax',
  'fanControlMin',
  'fanRotationMax',
  'fanRotationMin',
  'fanStepsMax',
  'fanStepsMin',
  'fanVfd',
  'fanMaxHz',
  'fanMinHz',
  'condWaterCooled',
  'condPressurDrop',
  'condApproachMax',
  'condApproachMin',
  'condScMax',
  'condScMin',
  'evapPressurDrop',
  'evapApproachMax',
  'evapApproachMin',
  'gearEexv',
  'exvLiquidInMax',
  'exvLiquidInMin',
  'exvLiquidOutMax',
  'exvLiquidOutMin',
  'eexvMainMax',
  'eexvMainMin',
  'eexvInjeMax',
  'eexvInjeMin',
  'eexvPulseMax',
  'eexvPulseMin',
  'eexvMax',
  'eexvMin',
  'ecoLiquidInMax',
  'ecoLiquidInMin',
  'ecoLiquidOutMax',
  'ecoLiquidOutMin',
  'ecoInjeInMax',
  'ecoInjeInMin',
  'ecoInjeOutMax',
  'ecoInjeOutMin',
  'contType',
  'contVoltageMax',
  'contVoltageMin',
  'typeElConn',
  'contCableType',
  'contCableDiameter',
  'contCableLengthMax',
  'mainPumpMax',
  'mainPumpMin',
  'mainPumpMaxA',
  'mainPumpMinA',
  'mainPumpPwmMax',
  'mainPumpPwmMin',
  'mainPumpMaxHz',
  'mainPumpMinHz',
  'bizonePumpMax',
  'bizonePumpMin',
  'bizonePumpMaxA',
  'bizonePumpMinA',
  'bizonePumpPwmMax',
  'bizonePumpPwmMin',
  'bizonePumpMaxHz',
  'bizonePumpMinHz',
  'threeWvVoltageMax',
  'threeWvVoltageMin',
  'threeWvMax',
  'threeWvMin',
  'threeWvBizone',
  'threeWvBizoneMax',
  'threeWvBizoneMin',
  'threeWvBizoneMaxKOhm',
  'threeWvBizoneMinKOhm',
  'threeWvGeneratorMax',
  'threeWvGeneratorMin',
  'threeWvGeneratorMaxKOhm',
  'threeWvGeneratorMinKOhm',
  'main4wvMax',
  'main4wvMin',
  'main4wvMaxKOhm',
  'main4wvMinKOhm',
  'sub4wvMax',
  'sub4wvMin',
  'sub4wvMaxKOhm',
  'sub4wvMinKOhm',
  'mainPcbPowerMax',
  'mainPcbPowerMin',
  'mainPcbFanMax',
  'mainPcbFanMin',
  'mainPcbRectifierMax',
  'mainPcbRectifierMin',
  'mainPcbV1rMax',
  'mainPcbV1rMin',
  'mainPcbV2rMax',
  'mainPcbV2rMin',
  'mainPcbV3rMax',
  'mainPcbV3rMin',
  'mainPcbV4rMax',
  'mainPcbV4rMin',
  'noisePcbPowerMax',
  'noisePcbPowerMin',
  'noisePcbLaMax',
  'noisePcbLaMin',
  'noisePcbLbMax',
  'noisePcbLbMin',
  'acspcbMax',
  'acspcbMin',
  'bizonePcbMax',
  'bizonePcbMin',
  'currentLoopPcbMax',
  'currentLoopPcbMin',
  'flashPcbMax',
  'flashPcbMin',
  'hydroPcbMax',
  'hydroPcbMin',
  'hydroExtPcbMax',
  'hydroExtPcbMin',
  'invpcbLaMax',
  'invpcbLaMin',
  'invpcbLbMax',
  'invpcbLbMin',
  'interfacePcbMax',
  'interfacePcbMin',
  'leakPcbMax',
  'leakPcbMin',
  'leakPcbMaxOhm',
  'leakPcbMinOhm',
  'bhupcbControlMax',
  'bhupcbControlMin',
  'bhupcbPowerMax',
  'bhupcbPowerMin',
  'powerSupplyPcbMax',
  'powerSupplyPcbMin',
  'powerSupplyPcbPcbMax',
  'powerSupplyPcbPcbMin',
  'reactorMax',
  'reactorMin',
  'buhType',
  'buh3v',
  'buh3vFuseRate',
  'buh3vMax',
  'buh3vMin',
  'buh6v1n',
  'buh6v1nFuseRate',
  'buh6v1nMax',
  'buh6v1nMin',
  'buh6v3n',
  'buh6v3nFuseRate',
  'buh6v3nMax',
  'buh6v3nMin',
  'buh9w',
  'buh9wFuseRate',
  'buh9wMax',
  'buh9wMin',
  'bshMax',
  'bshMin',
  'bshMaxOhm',
  'bshMinOhm',
  'reliefValve',
  'reliefValveDegC',
  'antiFreezeValve',
  'buhThermalProtector',
  'bshThermalProtector',
  'hps1OperatingMax',
  'hps1OperatingMin',
  'hps1ResetMax',
  'hps1ResetMin',
  'hps1VoltageMax',
  'hps1VoltageMin',
  'hps2OperatingMax',
  'hps2OperatingMin',
  'hps2ResetMax',
  'hps2ResetMin',
  'hps2VoltageMax',
  'hps2VoltageMin',
  'lps1OperatingMax',
  'lps1OperatingMin',
  'lps1ResetMax',
  'lps1ResetMin',
  'lps1VoltageMax',
  'lps1VoltageMin',
  'lps2OperatingMax',
  'lps2OperatingMin',
  'lps2ResetMax',
  'lps2ResetMin',
  'lps2VoltageMax',
  'lps2VoltageMin',
  'lps2ConnectedTo',
  'lps2IntermediateConnect',
  'hpSensorMax',
  'hpSensorMin',
  'hpSensorReadMax',
  'hpSensorReadMin',
  'lpSensorMax',
  'lpSensorMin',
  'lpSensorReadMax',
  'lpSensorReadMin',
  'wpSensorMax',
  'wpSensorMin',
  'wpSensorReadMax',
  'wpSensorReadMin',
  'svMax',
  'svMin',
  'svMaxKOhm',
  'svMinKOhm',
  'refThermistorAMax',
  'refThermistorAMin',
  'refThermistorBMax',
  'refThermistorBMin',
  'refThermistorCMax',
  'refThermistorCMin',
  'waterThermistorAMax',
  'waterThermistorAMin',
  'waterThermistorBMax',
  'waterThermistorBMin',
  'waterThermistorCMax',
  'waterThermistorCMin',
  'flowSensorMax',
  'flowSensorMin',
  'flowSensorMaxHz',
  'flowSensorMinHz',
  'gasSensorIuMax',
  'gasSensorIuMix',
  'gasSensorIuMaxPct',
  'gasSensorIuMixPct',
  'gasSensorOuMax',
  'gasSensorOuMix',
  'gasSensorOuMaxPct',
  'gasSensorOuMixPct',
  'refrigerantRectifier',
  'refrigerantRectifierMax',
  'refrigerantRectifierMin',
  'ekhvconvReversibleKitInstalled',
  'boilerDaikin',
  'boilerVentDiameter',
  'boilerFlueDiameter',
  'boilerVentLengthMax',
  'boilerFlueLengthMax',
  'boilerFlueVentMin',
  'boilerGasPressier',
  'boilerCo2HMax',
  'boilerCo2HMin',
  'boilerCo2LMax',
  'boilerCo2LMin',
  'boilerMax',
  'boilerMin',
];

export const CSV_HEADERS = [
  'Product_name',
  'Pillar',
  'Product_range',
  'Unit_Type',
  'Mount_Type',
  'Monobloc_Large',
  'Monobloc_largewithoutBUH',
  'UK_Model',
  'Capacity_Class',
  'Heating_Only',
  'Cooling_Only',
  'HP_Class',
  'Refrigerating_Ton[USRT]',
  'Cooling_Capacity_Max[kW]',
  'Cooling_Capacity_Min[kW]',
  'Heating_Capacity_Max[kW]',
  'Heating_Capacity_Min[kW]',
  'EER_Max',
  'EER_Min',
  'COP_Max',
  'COP_Min',
  'SEER_Max',
  'SEER_Min',
  'SCOP_Max',
  'SCOP_Min',
  'Sound_Max[dB(A)]',
  'Left_Min[mm]',
  'Right_Min[mm]',
  'Top_Min[mm]',
  'Bottom_Min[mm]',
  'Front_Min[mm]',
  'Back_Min[mm]',
  'BtwOpen_Min[mm]',
  'BtwUnit_Min[mm]',
  'CL_Outdoor_Max[℃]',
  'CL_Outdoor_Min[℃]',
  'HT_Outdoor_Max[℃]',
  'HT_Outdoor_Min[℃]',
  'CL_Indoor_Max[℃]',
  'CL_Indoor_Min[℃]',
  'HT_Indoor_Max[℃]',
  'HT_Indoor_Min[℃]',
  'Humidity_Max[%]',
  'Ref_Type',
  'Ref_GWP',
  'Ref_Charge_Nom[kg]',
  'Ref_Charge_Max[kg]',
  'Ref_Charge_Min[kg]',
  'Ref_Additional_Max[kg]',
  'Ref_Additional_Min[kg]',
  'Ref_Circuits',
  'Ref_Liquid_Diameter[mm]',
  'Ref_Gas_Diameter[mm]',
  'Ref_HP/LP_Diameter[mm]',
  'Ref_Length_Max[m]',
  'Ref_Length_Min[m]',
  'Ref_Height_Max[m]',
  'Ref_HP_Max[MPaG]',
  'Ref_HP_Min[MPaG]',
  'Ref_LP_Max[MPaG]',
  'Ref_LP_Min[MPaG]',
  'Ref_HP/LP_Max[MPaG]',
  'Ref_HP/LP_Min[MPaG]',
  'Heating_Min[L]',
  'Cooling_Min[L]',
  'EW_Min[L]',
  'CW_Min[L]',
  'Heating_Max[L/m]',
  'Heating_Min[L/m]',
  'Cooling_Max[L/m]',
  'Cooling_Min[L/m]',
  'EW_Max[L/m]',
  'EW_Min[L/m]',
  'CW_Max[L/m]',
  'CW_Min[L/m]',
  'HT_WaterPre_Max[bar]',
  'HT_WaterPre_Min[bar]',
  'DH_WaterPre_Max[bar]',
  'DH_WaterPre_Min[bar]',
  'EW_WaterPre_Max[bar]',
  'EW_WaterPre_Min[bar]',
  'CW_WaterPre_Max[bar]',
  'CW_WaterPre_Min[bar]',
  'HT_WaterTemp_Max[℃]',
  'HT_WaterTemp_Min[℃]',
  'DH_WaterTemp_Max[℃]',
  'DH_WaterTemp_Min[℃]',
  'EW_WaterTemp_Max[℃]',
  'EW_WaterTemp_Min[℃]',
  'CW_WaterTemp_Max[℃]',
  'CW_WaterTemp_Min[℃]',
  'Glycol_Max[%]',
  'Glycol_Min[%]',
  'Glycol_Max[℃]',
  'Glycol_Min[℃]',
  'IU-OU_Water_Diameter[mm]',
  'SH_Diameter[mm]',
  'WS_Diameter[mm]',
  'GEO_Diameter[mm]',
  'Cond_Water_Diameter[mm]',
  'Evap_Water_Diameter[mm]',
  'Drain_Diameter[mm]',
  'Drain_Height_Max[mm]',
  'Supply_Water_Diameter[mm]',
  'MagneticFilter_DirtSeparator',
  'Auto_Airpurge',
  'Manual_Airpurge',
  'Unit_Power_Max[V]',
  'Unit_Power_Min[V]',
  'Unit_Current_Max[A]',
  'Unit_FuseRate[A]',
  'Unit_Phase',
  'Unit_Frequency[Hz]',
  'Unit_Power_Cable_Diameter[mm]',
  'IU-OU_Cable_Diameter[mm]',
  'Unit_Power_Cable_Area[mm2]',
  'IU-OU_Cable_Area[mm2]',
  'IU-OU_Cable_Length_Max[m]',
  'Comp_Type',
  'Comp_Model_1',
  'Comp_Model_2',
  'Comp_Qty',
  'Comp_Max[V]',
  'Comp_Min[V]',
  'Comp1_Max[A]',
  'Comp1_Min[A]',
  'Comp_Max[A]',
  'Comp_2_Min[A]',
  'Comp_Steps_Max',
  'Comp_Steps_Min',
  'Comp_Max[Hz]',
  'Comp_Min[Hz]',
  'Comp_Heater_Max[V]',
  'Comp_Heater_Min[V]',
  'Comp_Heater_Max[A]',
  'Comp_Heater_Min[A]',
  'Comp_Heater_ON[℃]',
  'Comp_Heater_OFF[℃]',
  'Comp_Heater_Max[kΩ]',
  'Comp_Heater_Min[kΩ]',
  'Comp_Discharge_Max[℃]',
  'Comp_Discharge_Min[℃]',
  'Comp_Suction_Max[℃]',
  'Comp_Suction_Min[℃]',
  'Comp_Body_Max[℃]',
  'Comp_Body_Min[℃]',
  'Comp_DSH_Max[℃]',
  'Comp_SSH_Min[℃]',
  'Comp_SSH_Max[℃]',
  'Comp_DSH_Min[℃]',
  'Comp_U-V_Max[Ω]',
  'Comp_U-V_Min[Ω]',
  'Comp_V-W_Max[Ω]',
  'Comp_V-W_Min[Ω]',
  'Comp_U-W_Max[Ω]',
  'Comp_U-W_Min[Ω]',
  'Fan_DC',
  'Fan_Qty',
  'Fan_AirFlow_Max[m3/min]',
  'Fan_AirFlow_Min[m3/min]',
  'Fan_Max[V]',
  'Fan_Min[V]',
  'Fan_Max[A]',
  'Fan_Min[A]',
  'Fan_Max[Ω]',
  'Fan_Min[Ω]',
  'Fan_Control_Max[V]',
  'Fan_Control_Min[V]',
  'Fan_Rotation_Max[V]',
  'Fan_Rotation_Min[V]',
  'Fan_Steps_Max',
  'Fan_Steps_Min',
  'Fan_VFD',
  'Fan_Max[Hz]',
  'Fan_Min[Hz]',
  'Cond_WaterCooled',
  'Cond_Pressur_Drop[kPa]',
  'Cond_Approach_Max[℃]',
  'Cond_Approach_Min[℃]',
  'Cond_SC_Max[℃]',
  'Cond_SC_Min[℃]',
  'Evap_Pressur_Drop[kPa]',
  'Evap_Approach_Max[℃]',
  'Evap_Approach_Min[℃]',
  'Gear_EEXV',
  'EXV_Liquid_IN_Max[℃]',
  'EXV_Liquid_IN_Min[℃]',
  'EXV_Liquid_OutMax[℃]',
  'EXV_Liquid_OutMin[℃]',
  'EEXV_Main_Max[Ω]',
  'EEXV_Main_Min[Ω]',
  'EEXV_Inje_Max[Ω]',
  'EEXV_Inje_Min[Ω]',
  'EEXV_Pulse_Max',
  'EEXV_Pulse_Min',
  'EEXV_Max[V]',
  'EEXV_Min[V]',
  'Eco_Liquid_IN_Max[℃]',
  'Eco_Liquid_IN_Min[℃]',
  'Eco_Liquid_OUT_Max[℃]',
  'Eco_Liquid_OUT_Min[℃]',
  'Eco_Inje_IN_Max[℃]',
  'Eco_Inje_IN_Min[℃]',
  'Eco_Inje_OUT_Max[℃]',
  'Eco_Inje_OUT_Min[℃]',
  'Cont_Type',
  'Cont_Voltage_Max[V]',
  'Cont_Voltage_Min[V]',
  'TypeElConn',
  'Cont_Cable_Type',
  'Cont_Cable_Diameter[mm]',
  'Cont_Cable_Length_Max[m]',
  'MainPump_Max[V]',
  'MainPump_Min[V]',
  'MainPump_Max[A]',
  'MainPump_Min[A]',
  'MainPump_PWM_Max[%]',
  'MainPump_PWM_Min[%]',
  'MainPump_Max[Hz]',
  'MainPump_Min[Hz]',
  'BizonePump_Max[V]',
  'BizonePump_Min[V]',
  'BizonePump_Max[A]',
  'BizonePump_Min[A]',
  'BizonePump_PWM_Max[%]',
  'BizonePump_PWM_Min[%]',
  'BizonePump_Max[Hz]',
  'BizonePump_Min[Hz]',
  '3WV_Voltage_Max[V]',
  '3WV_Voltage_Min[V]',
  '3WV_Max[kΩ]',
  '3WV_Min[kΩ]',
  '3WV_Bizone',
  '3WV_Bizone_Max[V]',
  '3WV_Bizone_Min[V]',
  '3WV_Bizone_Max[kΩ]',
  '3WV_Bizone_Min[kΩ]',
  '3WV_Generator_Max[V]',
  '3WV_Generator_Min[V]',
  '3WV_Generator_Max[kΩ]',
  '3WV_Generator_Min[kΩ]',
  'Main4WV_Max[V]',
  'Main4WV_Min[V]',
  'Main4WV_Max[kΩ]',
  'Main4WV_Min[kΩ]',
  'Sub4WV_Max[V]',
  'Sub4WV_Min[V]',
  'Sub4WV_Max[kΩ]',
  'Sub4WV_Min[kΩ]',
  'MainPCB_Power_Max[V]',
  'MainPCB_Power_Min[V]',
  'MainPCB_Fan_Max[V]',
  'MainPCB_Fan_Min[V]',
  'MainPCB_Rectifier_Max[V]',
  'MainPCB_Rectifier_Min[V]',
  'MainPCB_V1R_Max[V]',
  'MainPCB_V1R_Min[V]',
  'MainPCB_V2R_Max[V]',
  'MainPCB_V2R_Min[V]',
  'MainPCB_V3R_Max[V]',
  'MainPCB_V3R_Min[V]',
  'MainPCB_V4R_Max[V]',
  'MainPCB_V4R_Min[V]',
  'NoisePCB_Power_Max[V]',
  'NoisePCB_Power_Min[V]',
  'NoisePCB_LA_Max[V]',
  'NoisePCB_LA_Min[V]',
  'NoisePCB_LB_Max[V]',
  'NoisePCB_LB_Min[V]',
  'ACSPCB_Max[V]',
  'ACSPCB_Min[V]',
  'BizonePCB_Max[V]',
  'BizonePCB_Min[V]',
  'CurrentLoopPCB_Max[V]',
  'CurrentLoopPCB_Min[V]',
  'FlashPCB_Max[V]',
  'FlashPCB_Min[V]',
  'HydroPCB_Max[V]',
  'HydroPCB_Min[V]',
  'HydroExtPCB_Max[V]',
  'HydroExtPCB_Min[V]',
  'INVPCB_LA_Max[V]',
  'INVPCB_LA_Min[V]',
  'INVPCB_LB_Max[V]',
  'INVPCB_LB_Min[V]',
  'Interface_PCB_Max[V]',
  'Interface_PCB_Min[V]',
  'LeakPCB_Max[V]',
  'LeakPCB_Min[V]',
  'LeakPCB_Max[Ω]',
  'LeakPCB_Min[Ω]',
  'BHUPCB_Control_Max[V]',
  'BHUPCB_Control_Min[V]',
  'BHUPCB_Power_Max[V]',
  'BHUPCB_Power_Min[V]',
  'PowerSupplyPCB_Max[V]',
  'PowerSupplyPCB_Min[V]',
  'PowerSupplyPCB_PCB_Max[V]',
  'PowerSupplyPCB_PCB_Min[V]',
  'Reactor_Max[mΩ]',
  'Reactor_Min[mΩ]',
  'BUH_Type',
  'BUH_3V',
  'BUH_3V_Fuse_Rate[A]',
  'BUH_3V_Max[V]',
  'BUH_3V_Min[V]',
  'BUH_6V1N',
  'BUH_6V1N_Fuse_Rate[A]',
  'BUH_6V1N_Max[V]',
  'BUH_6V1N_Min[V]',
  'BUH_6V3N',
  'BUH_6V3N_Fuse_Rate[A]',
  'BUH_6V3N_Max[V]',
  'BUH_6V3N_Min[V]',
  'BUH_9W',
  'BUH_9W_Fuse_Rate[A]',
  'BUH_9W_Max[V]',
  'BUH_9W_Min[V]',
  'BSH_Max[V]',
  'BSH_Min[V]',
  'BSH_Max[Ω]',
  'BSH_Min[Ω]',
  'Relief_Valve[kPa]',
  'Relief_Valve[℃]',
  'AntiFreezeValve[℃]',
  'BUH_Thermal_Protector[℃]',
  'BSH_Thermal_Protector[℃]',
  'HPS1_Operating_Max[MPaG]',
  'HPS1_Operating_Min[MPaG]',
  'HPS1_Reset_Max[MPaG]',
  'HPS1_Reset_Min[MPaG]',
  'HPS1_Voltage_Max[V]',
  'HPS1_Voltage_Min[V]',
  'HPS2_Operating_Max[MPaG]',
  'HPS2_Operating_Min[MPaG]',
  'HPS2_Reset_Max[MPaG]',
  'HPS2_Reset_Min[MPaG]',
  'HPS2_Voltage_Max[V]',
  'HPS2_Voltage_Min[V]',
  'LPS1_Operating_Max[MPaG]',
  'LPS1_Operating_Min[MPaG]',
  'LPS1_Reset_Max[MPaG]',
  'LPS1_Reset_Min[MPaG]',
  'LPS1_Voltage_Max[V]',
  'LPS1_Voltage_Min[V]',
  'LPS2_Operating_Max[MPaG]',
  'LPS2_Operating_Min[MPaG]',
  'LPS2_Reset_Max[MPaG]',
  'LPS2_Reset_Min[MPaG]',
  'LPS2_Voltage_Max[V]',
  'LPS2_Voltage_Min[V]',
  'LPS2_ConnectedTo',
  'LPS2_Intermediate_Connect',
  'HP_Sensor_Max[V]',
  'HP_Sensor_Min[V]',
  'HP_Sensor_Read_Max[V]',
  'HP_Sensor_Read_Min[V]',
  'LP_Sensor_Max[V]',
  'LP_Sensor_Min[V]',
  'LP_Sensor_Read_Max[V]',
  'LP_Sensor_Read_Min[V]',
  'WP_Sensor_Max[V]',
  'WP_Sensor_Min[V]',
  'WP_Sensor_Read_Max[V]',
  'WP_Sensor_Read_Min[V]',
  'SV_Max[V]',
  'SV_Min[V]',
  'SV_Max[kΩ]',
  'SV_Min[kΩ]',
  'Ref_Thermistor_A_Max[kΩ]',
  'Ref_Thermistor_A_Min[kΩ]',
  'Ref_Thermistor_B_Max[kΩ]',
  'Ref_Thermistor_B_Min[kΩ]',
  'Ref_Thermistor_C_Max[kΩ]',
  'Ref_Thermistor_C_Min[kΩ]',
  'Water_Thermistor_A_Max[kΩ]',
  'Water_Thermistor_A_Min[kΩ]',
  'Water_Thermistor_B_Max[kΩ]',
  'Water_Thermistor_B_Min[kΩ]',
  'Water_Thermistor_C_Max[kΩ]',
  'Water_Thermistor_C_Min[kΩ]',
  'FlowSensor_Max[V]',
  'FlowSensor_Min[V]',
  'FlowSensor_Max[Hz]',
  'FlowSensor_Min[Hz]',
  'GasSensor_IU_Max[V]',
  'GasSensor_IU_Mix[V]',
  'GasSensor_IU_Max[%]',
  'GasSensor_IU_Mix[%]',
  'GasSensor_OU_Max[V]',
  'GasSensor_OU_Mix[V]',
  'GasSensor_OU_Max[%]',
  'GasSensor_OU_Mix[%]',
  'Refrigerant_Rectifier',
  'Refrigerant_Rectifier_Max[K]',
  'Refrigerant_Rectifier_Min[K]',
  'EkhvconvReversibleKitInstalled',
  'Boiler_Daikin',
  'Boiler_Vent_Diameter[mm]',
  'Boiler_Flue_Diameter[mm]',
  'Boiler_Vent_Length_Max[m]',
  'Boiler_Flue_Length_Max[m]',
  'Boiler_Flue_Vent_Min[mm]',
  'Boiler_Gas_Pressier[mbar]',
  'Boiler_CO2_H_Max[%]',
  'Boiler_CO2_H_Min[%]',
  'Boiler_CO2_L_Max[%]',
  'Boiler_CO2_L_Min[%]',
  'Boiler_Max[kW]',
  'Boiler_Min[kW]',
] as const;

export const CSV_TRANSFORM_HEADER_MAP = {
  ['Product_name']: 'productName',
  ['Pillar']: 'pillar',
  ['Product_range']: 'productRange',
  ['Unit_Type']: 'unitType',
  ['Mount_Type']: 'mountType',
  ['Monobloc_Large']: 'monoblocLarge',
  ['Monobloc_largewithoutBUH']: 'monoblocLargeWithoutBuh',
  ['UK_Model']: 'ukModel',
  ['Capacity_Class']: 'capacityClass',
  ['Heating_Only']: 'heatingOnly',
  ['Cooling_Only']: 'coolingOnly',
  ['HP_Class']: 'hpClass',
  ['Refrigerating_Ton[USRT]']: 'refrigeratingTon',
  ['Cooling_Capacity_Max[kW]']: 'coolingCapacityMax',
  ['Cooling_Capacity_Min[kW]']: 'coolingCapacityMin',
  ['Heating_Capacity_Max[kW]']: 'heatingCapacityMax',
  ['Heating_Capacity_Min[kW]']: 'heatingCapacityMin',
  ['EER_Max']: 'eerMax',
  ['EER_Min']: 'eerMin',
  ['COP_Max']: 'copMax',
  ['COP_Min']: 'copMin',
  ['SEER_Max']: 'seerMax',
  ['SEER_Min']: 'seerMin',
  ['SCOP_Max']: 'scopMax',
  ['SCOP_Min']: 'scopMin',
  ['Sound_Max[dB(A)]']: 'soundMax',
  ['Left_Min[mm]']: 'leftMin',
  ['Right_Min[mm]']: 'rightMin',
  ['Top_Min[mm]']: 'topMin',
  ['Bottom_Min[mm]']: 'bottomMin',
  ['Front_Min[mm]']: 'frontMin',
  ['Back_Min[mm]']: 'backMin',
  ['BtwOpen_Min[mm]']: 'btwOpenMin',
  ['BtwUnit_Min[mm]']: 'btwUnitMin',
  ['CL_Outdoor_Max[℃]']: 'clOutdoorMax',
  ['CL_Outdoor_Min[℃]']: 'clOutdoorMin',
  ['HT_Outdoor_Max[℃]']: 'htOutdoorMax',
  ['HT_Outdoor_Min[℃]']: 'htOutdoorMin',
  ['CL_Indoor_Max[℃]']: 'clIndoorMax',
  ['CL_Indoor_Min[℃]']: 'clIndoorMin',
  ['HT_Indoor_Max[℃]']: 'htIndoorMax',
  ['HT_Indoor_Min[℃]']: 'htIndoorMin',
  ['Humidity_Max[%]']: 'humidityMax',
  ['Ref_Type']: 'refType',
  ['Ref_GWP']: 'refGwp',
  ['Ref_Charge_Nom[kg]']: 'refChargeNom',
  ['Ref_Charge_Max[kg]']: 'refChargeMax',
  ['Ref_Charge_Min[kg]']: 'refChargeMin',
  ['Ref_Additional_Max[kg]']: 'refAdditionalMax',
  ['Ref_Additional_Min[kg]']: 'refAdditionalMin',
  ['Ref_Circuits']: 'refCircuits',
  ['Ref_Liquid_Diameter[mm]']: 'refLiquidDiameter',
  ['Ref_Gas_Diameter[mm]']: 'refGasDiameter',
  ['Ref_HP/LP_Diameter[mm]']: 'refHpLpDiameter',
  ['Ref_Length_Max[m]']: 'refLengthMax',
  ['Ref_Length_Min[m]']: 'refLengthMin',
  ['Ref_Height_Max[m]']: 'refHeightMax',
  ['Ref_HP_Max[MPaG]']: 'refHpMax',
  ['Ref_HP_Min[MPaG]']: 'refHpMin',
  ['Ref_LP_Max[MPaG]']: 'refLpMax',
  ['Ref_LP_Min[MPaG]']: 'refLpMin',
  ['Ref_HP/LP_Max[MPaG]']: 'refHpLpMax',
  ['Ref_HP/LP_Min[MPaG]']: 'refHpLpMin',
  ['Heating_Min[L]']: 'heatingMin',
  ['Cooling_Min[L]']: 'coolingMin',
  ['EW_Min[L]']: 'ewMin',
  ['CW_Min[L]']: 'cwMin',
  ['Heating_Max[L/m]']: 'heatingMaxLm',
  ['Heating_Min[L/m]']: 'heatingMinLm',
  ['Cooling_Max[L/m]']: 'coolingMaxLm',
  ['Cooling_Min[L/m]']: 'coolingMinLm',
  ['EW_Max[L/m]']: 'ewMaxLm',
  ['EW_Min[L/m]']: 'ewMinLm',
  ['CW_Max[L/m]']: 'cwMaxLm',
  ['CW_Min[L/m]']: 'cwMinLm',
  ['HT_WaterPre_Max[bar]']: 'htWaterPreMax',
  ['HT_WaterPre_Min[bar]']: 'htWaterPreMin',
  ['DH_WaterPre_Max[bar]']: 'dhWaterPreMax',
  ['DH_WaterPre_Min[bar]']: 'dhWaterPreMin',
  ['EW_WaterPre_Max[bar]']: 'ewWaterPreMax',
  ['EW_WaterPre_Min[bar]']: 'ewWaterPreMin',
  ['CW_WaterPre_Max[bar]']: 'cwWaterPreMax',
  ['CW_WaterPre_Min[bar]']: 'cwWaterPreMin',
  ['HT_WaterTemp_Max[℃]']: 'htWaterTempMax',
  ['HT_WaterTemp_Min[℃]']: 'htWaterTempMin',
  ['DH_WaterTemp_Max[℃]']: 'dhWaterTempMax',
  ['DH_WaterTemp_Min[℃]']: 'dhWaterTempMin',
  ['EW_WaterTemp_Max[℃]']: 'ewWaterTempMax',
  ['EW_WaterTemp_Min[℃]']: 'ewWaterTempMin',
  ['CW_WaterTemp_Max[℃]']: 'cwWaterTempMax',
  ['CW_WaterTemp_Min[℃]']: 'cwWaterTempMin',
  ['Glycol_Max[%]']: 'glycolMax',
  ['Glycol_Min[%]']: 'glycolMin',
  ['Glycol_Max[℃]']: 'glycolMaxDegC',
  ['Glycol_Min[℃]']: 'glycolMinDegC',
  ['IU-OU_Water_Diameter[mm]']: 'iuOuWaterDiameter',
  ['SH_Diameter[mm]']: 'shDiameter',
  ['WS_Diameter[mm]']: 'wsDiameter',
  ['GEO_Diameter[mm]']: 'geoDiameter',
  ['Cond_Water_Diameter[mm]']: 'condWaterDiameter',
  ['Evap_Water_Diameter[mm]']: 'evapWaterDiameter',
  ['Drain_Diameter[mm]']: 'drainDiameter',
  ['Drain_Height_Max[mm]']: 'drainHeightMax',
  ['Supply_Water_Diameter[mm]']: 'supplyWaterDiameter',
  ['MagneticFilter_DirtSeparator']: 'magneticFilterDirtSeparator',
  ['Auto_Airpurge']: 'autoAirpurge',
  ['Manual_Airpurge']: 'manualAirpurge',
  ['Unit_Power_Max[V]']: 'unitPowerMax',
  ['Unit_Power_Min[V]']: 'unitPowerMin',
  ['Unit_Current_Max[A]']: 'unitCurrentMax',
  ['Unit_FuseRate[A]']: 'unitFuseRate',
  ['Unit_Phase']: 'unitPhase',
  ['Unit_Frequency[Hz]']: 'unitFrequency',
  ['Unit_Power_Cable_Diameter[mm]']: 'unitPowerCableDiameter',
  ['IU-OU_Cable_Diameter[mm]']: 'iuOuCableDiameter',
  ['Unit_Power_Cable_Area[mm2]']: 'unitPowerCableArea',
  ['IU-OU_Cable_Area[mm2]']: 'iuOuCableArea',
  ['IU-OU_Cable_Length_Max[m]']: 'iuOuCableLengthMax',
  ['Comp_Type']: 'compType',
  ['Comp_Model_1']: 'compModel1',
  ['Comp_Model_2']: 'compModel2',
  ['Comp_Qty']: 'compQty',
  ['Comp_Max[V]']: 'compMax',
  ['Comp_Min[V]']: 'compMin',
  ['Comp1_Max[A]']: 'comp1Max',
  ['Comp1_Min[A]']: 'comp1Min',
  ['Comp_Max[A]']: 'compMaxA',
  ['Comp_2_Min[A]']: 'comp2Min',
  ['Comp_Steps_Max']: 'compStepsMax',
  ['Comp_Steps_Min']: 'compStepsMin',
  ['Comp_Max[Hz]']: 'compMaxHz',
  ['Comp_Min[Hz]']: 'compMinHz',
  ['Comp_Heater_Max[V]']: 'compHeaterMax',
  ['Comp_Heater_Min[V]']: 'compHeaterMin',
  ['Comp_Heater_Max[A]']: 'compHeaterMaxA',
  ['Comp_Heater_Min[A]']: 'compHeaterMinA',
  ['Comp_Heater_ON[℃]']: 'compHeaterOn',
  ['Comp_Heater_OFF[℃]']: 'compHeaterOff',
  ['Comp_Heater_Max[kΩ]']: 'compHeaterMaxKOhm',
  ['Comp_Heater_Min[kΩ]']: 'compHeaterMinKOhm',
  ['Comp_Discharge_Max[℃]']: 'compDischargeMax',
  ['Comp_Discharge_Min[℃]']: 'compDischargeMin',
  ['Comp_Suction_Max[℃]']: 'compSuctionMax',
  ['Comp_Suction_Min[℃]']: 'compSuctionMin',
  ['Comp_Body_Max[℃]']: 'compBodyMax',
  ['Comp_Body_Min[℃]']: 'compBodyMin',
  ['Comp_DSH_Max[℃]']: 'compDshMax',
  ['Comp_SSH_Min[℃]']: 'compSshMin',
  ['Comp_SSH_Max[℃]']: 'compSshMax',
  ['Comp_DSH_Min[℃]']: 'compDshMin',
  ['Comp_U-V_Max[Ω]']: 'compUVMax',
  ['Comp_U-V_Min[Ω]']: 'compUVMin',
  ['Comp_V-W_Max[Ω]']: 'compVWMax',
  ['Comp_V-W_Min[Ω]']: 'compVWMin',
  ['Comp_U-W_Max[Ω]']: 'compUWMax',
  ['Comp_U-W_Min[Ω]']: 'compUWMin',
  ['Fan_DC']: 'fanDc',
  ['Fan_Qty']: 'fanQty',
  ['Fan_AirFlow_Max[m3/min]']: 'fanAirFlowMax',
  ['Fan_AirFlow_Min[m3/min]']: 'fanAirFlowMin',
  ['Fan_Max[V]']: 'fanMax',
  ['Fan_Min[V]']: 'fanMin',
  ['Fan_Max[A]']: 'fanMaxA',
  ['Fan_Min[A]']: 'fanMinA',
  ['Fan_Max[Ω]']: 'fanMaxOhm',
  ['Fan_Min[Ω]']: 'fanMinOhm',
  ['Fan_Control_Max[V]']: 'fanControlMax',
  ['Fan_Control_Min[V]']: 'fanControlMin',
  ['Fan_Rotation_Max[V]']: 'fanRotationMax',
  ['Fan_Rotation_Min[V]']: 'fanRotationMin',
  ['Fan_Steps_Max']: 'fanStepsMax',
  ['Fan_Steps_Min']: 'fanStepsMin',
  ['Fan_VFD']: 'fanVfd',
  ['Fan_Max[Hz]']: 'fanMaxHz',
  ['Fan_Min[Hz]']: 'fanMinHz',
  ['Cond_WaterCooled']: 'condWaterCooled',
  ['Cond_Pressur_Drop[kPa]']: 'condPressurDrop',
  ['Cond_Approach_Max[℃]']: 'condApproachMax',
  ['Cond_Approach_Min[℃]']: 'condApproachMin',
  ['Cond_SC_Max[℃]']: 'condScMax',
  ['Cond_SC_Min[℃]']: 'condScMin',
  ['Evap_Pressur_Drop[kPa]']: 'evapPressurDrop',
  ['Evap_Approach_Max[℃]']: 'evapApproachMax',
  ['Evap_Approach_Min[℃]']: 'evapApproachMin',
  ['Gear_EEXV']: 'gearEexv',
  ['EXV_Liquid_IN_Max[℃]']: 'exvLiquidInMax',
  ['EXV_Liquid_IN_Min[℃]']: 'exvLiquidInMin',
  ['EXV_Liquid_OutMax[℃]']: 'exvLiquidOutMax',
  ['EXV_Liquid_OutMin[℃]']: 'exvLiquidOutMin',
  ['EEXV_Main_Max[Ω]']: 'eexvMainMax',
  ['EEXV_Main_Min[Ω]']: 'eexvMainMin',
  ['EEXV_Inje_Max[Ω]']: 'eexvInjeMax',
  ['EEXV_Inje_Min[Ω]']: 'eexvInjeMin',
  ['EEXV_Pulse_Max']: 'eexvPulseMax',
  ['EEXV_Pulse_Min']: 'eexvPulseMin',
  ['EEXV_Max[V]']: 'eexvMax',
  ['EEXV_Min[V]']: 'eexvMin',
  ['Eco_Liquid_IN_Max[℃]']: 'ecoLiquidInMax',
  ['Eco_Liquid_IN_Min[℃]']: 'ecoLiquidInMin',
  ['Eco_Liquid_OUT_Max[℃]']: 'ecoLiquidOutMax',
  ['Eco_Liquid_OUT_Min[℃]']: 'ecoLiquidOutMin',
  ['Eco_Inje_IN_Max[℃]']: 'ecoInjeInMax',
  ['Eco_Inje_IN_Min[℃]']: 'ecoInjeInMin',
  ['Eco_Inje_OUT_Max[℃]']: 'ecoInjeOutMax',
  ['Eco_Inje_OUT_Min[℃]']: 'ecoInjeOutMin',
  ['Cont_Type']: 'contType',
  ['Cont_Voltage_Max[V]']: 'contVoltageMax',
  ['Cont_Voltage_Min[V]']: 'contVoltageMin',
  ['TypeElConn']: 'typeElConn',
  ['Cont_Cable_Type']: 'contCableType',
  ['Cont_Cable_Diameter[mm]']: 'contCableDiameter',
  ['Cont_Cable_Length_Max[m]']: 'contCableLengthMax',
  ['MainPump_Max[V]']: 'mainPumpMax',
  ['MainPump_Min[V]']: 'mainPumpMin',
  ['MainPump_Max[A]']: 'mainPumpMaxA',
  ['MainPump_Min[A]']: 'mainPumpMinA',
  ['MainPump_PWM_Max[%]']: 'mainPumpPwmMax',
  ['MainPump_PWM_Min[%]']: 'mainPumpPwmMin',
  ['MainPump_Max[Hz]']: 'mainPumpMaxHz',
  ['MainPump_Min[Hz]']: 'mainPumpMinHz',
  ['BizonePump_Max[V]']: 'bizonePumpMax',
  ['BizonePump_Min[V]']: 'bizonePumpMin',
  ['BizonePump_Max[A]']: 'bizonePumpMaxA',
  ['BizonePump_Min[A]']: 'bizonePumpMinA',
  ['BizonePump_PWM_Max[%]']: 'bizonePumpPwmMax',
  ['BizonePump_PWM_Min[%]']: 'bizonePumpPwmMin',
  ['BizonePump_Max[Hz]']: 'bizonePumpMaxHz',
  ['BizonePump_Min[Hz]']: 'bizonePumpMinHz',
  ['3WV_Voltage_Max[V]']: 'threeWvVoltageMax',
  ['3WV_Voltage_Min[V]']: 'threeWvVoltageMin',
  ['3WV_Max[kΩ]']: 'threeWvMax',
  ['3WV_Min[kΩ]']: 'threeWvMin',
  ['3WV_Bizone']: 'threeWvBizone',
  ['3WV_Bizone_Max[V]']: 'threeWvBizoneMax',
  ['3WV_Bizone_Min[V]']: 'threeWvBizoneMin',
  ['3WV_Bizone_Max[kΩ]']: 'threeWvBizoneMaxKOhm',
  ['3WV_Bizone_Min[kΩ]']: 'threeWvBizoneMinKOhm',
  ['3WV_Generator_Max[V]']: 'threeWvGeneratorMax',
  ['3WV_Generator_Min[V]']: 'threeWvGeneratorMin',
  ['3WV_Generator_Max[kΩ]']: 'threeWvGeneratorMaxKOhm',
  ['3WV_Generator_Min[kΩ]']: 'threeWvGeneratorMinKOhm',
  ['Main4WV_Max[V]']: 'main4wvMax',
  ['Main4WV_Min[V]']: 'main4wvMin',
  ['Main4WV_Max[kΩ]']: 'main4wvMaxKOhm',
  ['Main4WV_Min[kΩ]']: 'main4wvMinKOhm',
  ['Sub4WV_Max[V]']: 'sub4wvMax',
  ['Sub4WV_Min[V]']: 'sub4wvMin',
  ['Sub4WV_Max[kΩ]']: 'sub4wvMaxKOhm',
  ['Sub4WV_Min[kΩ]']: 'sub4wvMinKOhm',
  ['MainPCB_Power_Max[V]']: 'mainPcbPowerMax',
  ['MainPCB_Power_Min[V]']: 'mainPcbPowerMin',
  ['MainPCB_Fan_Max[V]']: 'mainPcbFanMax',
  ['MainPCB_Fan_Min[V]']: 'mainPcbFanMin',
  ['MainPCB_Rectifier_Max[V]']: 'mainPcbRectifierMax',
  ['MainPCB_Rectifier_Min[V]']: 'mainPcbRectifierMin',
  ['MainPCB_V1R_Max[V]']: 'mainPcbV1rMax',
  ['MainPCB_V1R_Min[V]']: 'mainPcbV1rMin',
  ['MainPCB_V2R_Max[V]']: 'mainPcbV2rMax',
  ['MainPCB_V2R_Min[V]']: 'mainPcbV2rMin',
  ['MainPCB_V3R_Max[V]']: 'mainPcbV3rMax',
  ['MainPCB_V3R_Min[V]']: 'mainPcbV3rMin',
  ['MainPCB_V4R_Max[V]']: 'mainPcbV4rMax',
  ['MainPCB_V4R_Min[V]']: 'mainPcbV4rMin',
  ['NoisePCB_Power_Max[V]']: 'noisePcbPowerMax',
  ['NoisePCB_Power_Min[V]']: 'noisePcbPowerMin',
  ['NoisePCB_LA_Max[V]']: 'noisePcbLaMax',
  ['NoisePCB_LA_Min[V]']: 'noisePcbLaMin',
  ['NoisePCB_LB_Max[V]']: 'noisePcbLbMax',
  ['NoisePCB_LB_Min[V]']: 'noisePcbLbMin',
  ['ACSPCB_Max[V]']: 'acspcbMax',
  ['ACSPCB_Min[V]']: 'acspcbMin',
  ['BizonePCB_Max[V]']: 'bizonePcbMax',
  ['BizonePCB_Min[V]']: 'bizonePcbMin',
  ['CurrentLoopPCB_Max[V]']: 'currentLoopPcbMax',
  ['CurrentLoopPCB_Min[V]']: 'currentLoopPcbMin',
  ['FlashPCB_Max[V]']: 'flashPcbMax',
  ['FlashPCB_Min[V]']: 'flashPcbMin',
  ['HydroPCB_Max[V]']: 'hydroPcbMax',
  ['HydroPCB_Min[V]']: 'hydroPcbMin',
  ['HydroExtPCB_Max[V]']: 'hydroExtPcbMax',
  ['HydroExtPCB_Min[V]']: 'hydroExtPcbMin',
  ['INVPCB_LA_Max[V]']: 'invpcbLaMax',
  ['INVPCB_LA_Min[V]']: 'invpcbLaMin',
  ['INVPCB_LB_Max[V]']: 'invpcbLbMax',
  ['INVPCB_LB_Min[V]']: 'invpcbLbMin',
  ['Interface_PCB_Max[V]']: 'interfacePcbMax',
  ['Interface_PCB_Min[V]']: 'interfacePcbMin',
  ['LeakPCB_Max[V]']: 'leakPcbMax',
  ['LeakPCB_Min[V]']: 'leakPcbMin',
  ['LeakPCB_Max[Ω]']: 'leakPcbMaxOhm',
  ['LeakPCB_Min[Ω]']: 'leakPcbMinOhm',
  ['BHUPCB_Control_Max[V]']: 'bhupcbControlMax',
  ['BHUPCB_Control_Min[V]']: 'bhupcbControlMin',
  ['BHUPCB_Power_Max[V]']: 'bhupcbPowerMax',
  ['BHUPCB_Power_Min[V]']: 'bhupcbPowerMin',
  ['PowerSupplyPCB_Max[V]']: 'powerSupplyPcbMax',
  ['PowerSupplyPCB_Min[V]']: 'powerSupplyPcbMin',
  ['PowerSupplyPCB_PCB_Max[V]']: 'powerSupplyPcbPcbMax',
  ['PowerSupplyPCB_PCB_Min[V]']: 'powerSupplyPcbPcbMin',
  ['Reactor_Max[mΩ]']: 'reactorMax',
  ['Reactor_Min[mΩ]']: 'reactorMin',
  ['BUH_Type']: 'buhType',
  ['BUH_3V']: 'buh3v',
  ['BUH_3V_Fuse_Rate[A]']: 'buh3vFuseRate',
  ['BUH_3V_Max[V]']: 'buh3vMax',
  ['BUH_3V_Min[V]']: 'buh3vMin',
  ['BUH_6V1N']: 'buh6v1n',
  ['BUH_6V1N_Fuse_Rate[A]']: 'buh6v1nFuseRate',
  ['BUH_6V1N_Max[V]']: 'buh6v1nMax',
  ['BUH_6V1N_Min[V]']: 'buh6v1nMin',
  ['BUH_6V3N']: 'buh6v3n',
  ['BUH_6V3N_Fuse_Rate[A]']: 'buh6v3nFuseRate',
  ['BUH_6V3N_Max[V]']: 'buh6v3nMax',
  ['BUH_6V3N_Min[V]']: 'buh6v3nMin',
  ['BUH_9W']: 'buh9w',
  ['BUH_9W_Fuse_Rate[A]']: 'buh9wFuseRate',
  ['BUH_9W_Max[V]']: 'buh9wMax',
  ['BUH_9W_Min[V]']: 'buh9wMin',
  ['BSH_Max[V]']: 'bshMax',
  ['BSH_Min[V]']: 'bshMin',
  ['BSH_Max[Ω]']: 'bshMaxOhm',
  ['BSH_Min[Ω]']: 'bshMinOhm',
  ['Relief_Valve[kPa]']: 'reliefValve',
  ['Relief_Valve[℃]']: 'reliefValveDegC',
  ['AntiFreezeValve[℃]']: 'antiFreezeValve',
  ['BUH_Thermal_Protector[℃]']: 'buhThermalProtector',
  ['BSH_Thermal_Protector[℃]']: 'bshThermalProtector',
  ['HPS1_Operating_Max[MPaG]']: 'hps1OperatingMax',
  ['HPS1_Operating_Min[MPaG]']: 'hps1OperatingMin',
  ['HPS1_Reset_Max[MPaG]']: 'hps1ResetMax',
  ['HPS1_Reset_Min[MPaG]']: 'hps1ResetMin',
  ['HPS1_Voltage_Max[V]']: 'hps1VoltageMax',
  ['HPS1_Voltage_Min[V]']: 'hps1VoltageMin',
  ['HPS2_Operating_Max[MPaG]']: 'hps2OperatingMax',
  ['HPS2_Operating_Min[MPaG]']: 'hps2OperatingMin',
  ['HPS2_Reset_Max[MPaG]']: 'hps2ResetMax',
  ['HPS2_Reset_Min[MPaG]']: 'hps2ResetMin',
  ['HPS2_Voltage_Max[V]']: 'hps2VoltageMax',
  ['HPS2_Voltage_Min[V]']: 'hps2VoltageMin',
  ['LPS1_Operating_Max[MPaG]']: 'lps1OperatingMax',
  ['LPS1_Operating_Min[MPaG]']: 'lps1OperatingMin',
  ['LPS1_Reset_Max[MPaG]']: 'lps1ResetMax',
  ['LPS1_Reset_Min[MPaG]']: 'lps1ResetMin',
  ['LPS1_Voltage_Max[V]']: 'lps1VoltageMax',
  ['LPS1_Voltage_Min[V]']: 'lps1VoltageMin',
  ['LPS2_Operating_Max[MPaG]']: 'lps2OperatingMax',
  ['LPS2_Operating_Min[MPaG]']: 'lps2OperatingMin',
  ['LPS2_Reset_Max[MPaG]']: 'lps2ResetMax',
  ['LPS2_Reset_Min[MPaG]']: 'lps2ResetMin',
  ['LPS2_Voltage_Max[V]']: 'lps2VoltageMax',
  ['LPS2_Voltage_Min[V]']: 'lps2VoltageMin',
  ['LPS2_ConnectedTo']: 'lps2ConnectedTo',
  ['LPS2_Intermediate_Connect']: 'lps2IntermediateConnect',
  ['HP_Sensor_Max[V]']: 'hpSensorMax',
  ['HP_Sensor_Min[V]']: 'hpSensorMin',
  ['HP_Sensor_Read_Max[V]']: 'hpSensorReadMax',
  ['HP_Sensor_Read_Min[V]']: 'hpSensorReadMin',
  ['LP_Sensor_Max[V]']: 'lpSensorMax',
  ['LP_Sensor_Min[V]']: 'lpSensorMin',
  ['LP_Sensor_Read_Max[V]']: 'lpSensorReadMax',
  ['LP_Sensor_Read_Min[V]']: 'lpSensorReadMin',
  ['WP_Sensor_Max[V]']: 'wpSensorMax',
  ['WP_Sensor_Min[V]']: 'wpSensorMin',
  ['WP_Sensor_Read_Max[V]']: 'wpSensorReadMax',
  ['WP_Sensor_Read_Min[V]']: 'wpSensorReadMin',
  ['SV_Max[V]']: 'svMax',
  ['SV_Min[V]']: 'svMin',
  ['SV_Max[kΩ]']: 'svMaxKOhm',
  ['SV_Min[kΩ]']: 'svMinKOhm',
  ['Ref_Thermistor_A_Max[kΩ]']: 'refThermistorAMax',
  ['Ref_Thermistor_A_Min[kΩ]']: 'refThermistorAMin',
  ['Ref_Thermistor_B_Max[kΩ]']: 'refThermistorBMax',
  ['Ref_Thermistor_B_Min[kΩ]']: 'refThermistorBMin',
  ['Ref_Thermistor_C_Max[kΩ]']: 'refThermistorCMax',
  ['Ref_Thermistor_C_Min[kΩ]']: 'refThermistorCMin',
  ['Water_Thermistor_A_Max[kΩ]']: 'waterThermistorAMax',
  ['Water_Thermistor_A_Min[kΩ]']: 'waterThermistorAMin',
  ['Water_Thermistor_B_Max[kΩ]']: 'waterThermistorBMax',
  ['Water_Thermistor_B_Min[kΩ]']: 'waterThermistorBMin',
  ['Water_Thermistor_C_Max[kΩ]']: 'waterThermistorCMax',
  ['Water_Thermistor_C_Min[kΩ]']: 'waterThermistorCMin',
  ['FlowSensor_Max[V]']: 'flowSensorMax',
  ['FlowSensor_Min[V]']: 'flowSensorMin',
  ['FlowSensor_Max[Hz]']: 'flowSensorMaxHz',
  ['FlowSensor_Min[Hz]']: 'flowSensorMinHz',
  ['GasSensor_IU_Max[V]']: 'gasSensorIuMax',
  ['GasSensor_IU_Mix[V]']: 'gasSensorIuMix',
  ['GasSensor_IU_Max[%]']: 'gasSensorIuMaxPct',
  ['GasSensor_IU_Mix[%]']: 'gasSensorIuMixPct',
  ['GasSensor_OU_Max[V]']: 'gasSensorOuMax',
  ['GasSensor_OU_Mix[V]']: 'gasSensorOuMix',
  ['GasSensor_OU_Max[%]']: 'gasSensorOuMaxPct',
  ['GasSensor_OU_Mix[%]']: 'gasSensorOuMixPct',
  ['Refrigerant_Rectifier']: 'refrigerantRectifier',
  ['Refrigerant_Rectifier_Max[K]']: 'refrigerantRectifierMax',
  ['Refrigerant_Rectifier_Min[K]']: 'refrigerantRectifierMin',
  ['EkhvconvReversibleKitInstalled']: 'ekhvconvReversibleKitInstalled',
  ['Boiler_Daikin']: 'boilerDaikin',
  ['Boiler_Vent_Diameter[mm]']: 'boilerVentDiameter',
  ['Boiler_Flue_Diameter[mm]']: 'boilerFlueDiameter',
  ['Boiler_Vent_Length_Max[m]']: 'boilerVentLengthMax',
  ['Boiler_Flue_Length_Max[m]']: 'boilerFlueLengthMax',
  ['Boiler_Flue_Vent_Min[mm]']: 'boilerFlueVentMin',
  ['Boiler_Gas_Pressier[mbar]']: 'boilerGasPressier',
  ['Boiler_CO2_H_Max[%]']: 'boilerCo2HMax',
  ['Boiler_CO2_H_Min[%]']: 'boilerCo2HMin',
  ['Boiler_CO2_L_Max[%]']: 'boilerCo2LMax',
  ['Boiler_CO2_L_Min[%]']: 'boilerCo2LMin',
  ['Boiler_Max[kW]']: 'boilerMax',
  ['Boiler_Min[kW]']: 'boilerMin',
} as const;

type CsvFormat = {
  name: string;
  type: string;
  range?: {
    min: number;
    max: number;
    decimal: number;
  };
  required: boolean;
};
export const CSV_FORMAT: CsvFormat[] = [
  { name: 'productName', type: 'string', required: true },
  { name: 'pillar', type: 'string', required: true },
  { name: 'productRange', type: 'string', required: true },
  { name: 'unitType', type: 'string', required: true },
  { name: 'mountType', type: 'string', required: false },
  { name: 'monoblocLarge', type: 'bool', required: false },
  { name: 'monoblocLargeWithoutBuh', type: 'bool', required: false },
  { name: 'ukModel', type: 'bool', required: false },
  { name: 'capacityClass', type: 'number', range: { min: 0, max: 1000, decimal: 0 }, required: true },
  { name: 'heatingOnly', type: 'bool', required: false },
  { name: 'coolingOnly', type: 'bool', required: false },
  { name: 'hpClass', type: 'number', range: { min: 0, max: 1000, decimal: 0 }, required: false },
  { name: 'refrigeratingTon', type: 'number', range: { min: 0, max: 10000, decimal: 0 }, required: false },
  { name: 'coolingCapacityMax', type: 'number', range: { min: 0, max: 100000, decimal: 2 }, required: false },
  { name: 'coolingCapacityMin', type: 'number', range: { min: 0, max: 100000, decimal: 2 }, required: false },
  { name: 'heatingCapacityMax', type: 'number', range: { min: 0, max: 100000, decimal: 2 }, required: false },
  { name: 'heatingCapacityMin', type: 'number', range: { min: 0, max: 100000, decimal: 2 }, required: false },
  { name: 'eerMax', type: 'number', range: { min: 0, max: 100, decimal: 2 }, required: false },
  { name: 'eerMin', type: 'number', range: { min: 0, max: 100, decimal: 2 }, required: false },
  { name: 'copMax', type: 'number', range: { min: 0, max: 100, decimal: 2 }, required: false },
  { name: 'copMin', type: 'number', range: { min: 0, max: 100, decimal: 2 }, required: false },
  { name: 'seerMax', type: 'number', range: { min: 0, max: 100, decimal: 2 }, required: false },
  { name: 'seerMin', type: 'number', range: { min: 0, max: 100, decimal: 2 }, required: false },
  { name: 'scopMax', type: 'number', range: { min: 0, max: 100, decimal: 2 }, required: false },
  { name: 'scopMin', type: 'number', range: { min: 0, max: 100, decimal: 2 }, required: false },
  { name: 'soundMax', type: 'number', range: { min: 0, max: 100, decimal: 1 }, required: false },
  { name: 'leftMin', type: 'number', range: { min: 0, max: 10000, decimal: 0 }, required: false },
  { name: 'rightMin', type: 'number', range: { min: 0, max: 10000, decimal: 0 }, required: false },
  { name: 'topMin', type: 'number', range: { min: 0, max: 10000, decimal: 0 }, required: false },
  { name: 'bottomMin', type: 'number', range: { min: 0, max: 10000, decimal: 0 }, required: false },
  { name: 'frontMin', type: 'number', range: { min: 0, max: 10000, decimal: 0 }, required: false },
  { name: 'backMin', type: 'number', range: { min: 0, max: 10000, decimal: 0 }, required: false },
  { name: 'btwOpenMin', type: 'number', range: { min: 0, max: 10000, decimal: 0 }, required: false },
  { name: 'btwUnitMin', type: 'number', range: { min: 0, max: 10000, decimal: 0 }, required: false },
  { name: 'clOutdoorMax', type: 'number', range: { min: -100, max: 100, decimal: 0 }, required: false },
  { name: 'clOutdoorMin', type: 'number', range: { min: -100, max: 100, decimal: 0 }, required: false },
  { name: 'htOutdoorMax', type: 'number', range: { min: -100, max: 100, decimal: 0 }, required: false },
  { name: 'htOutdoorMin', type: 'number', range: { min: -100, max: 100, decimal: 0 }, required: false },
  { name: 'clIndoorMax', type: 'number', range: { min: -100, max: 100, decimal: 0 }, required: false },
  { name: 'clIndoorMin', type: 'number', range: { min: -100, max: 100, decimal: 0 }, required: false },
  { name: 'htIndoorMax', type: 'number', range: { min: -100, max: 100, decimal: 0 }, required: false },
  { name: 'htIndoorMin', type: 'number', range: { min: -100, max: 100, decimal: 0 }, required: false },
  { name: 'humidityMax', type: 'number', range: { min: 0, max: 100, decimal: 0 }, required: false },
  { name: 'refType', type: 'string', required: false },
  { name: 'refGwp', type: 'number', range: { min: 0, max: 10000, decimal: 1 }, required: false },
  { name: 'refChargeNom', type: 'number', range: { min: 0, max: 10000, decimal: 2 }, required: false },
  { name: 'refChargeMax', type: 'number', range: { min: 0, max: 10000, decimal: 2 }, required: false },
  { name: 'refChargeMin', type: 'number', range: { min: 0, max: 10000, decimal: 2 }, required: false },
  { name: 'refAdditionalMax', type: 'number', range: { min: 0, max: 10000, decimal: 1 }, required: false },
  { name: 'refAdditionalMin', type: 'number', range: { min: 0, max: 10000, decimal: 1 }, required: false },
  { name: 'refCircuits', type: 'number', range: { min: 0, max: 10, decimal: 0 }, required: false },
  { name: 'refLiquidDiameter', type: 'number', range: { min: 0, max: 100, decimal: 1 }, required: false },
  { name: 'refGasDiameter', type: 'number', range: { min: 0, max: 100, decimal: 1 }, required: false },
  { name: 'refHpLpDiameter', type: 'number', range: { min: 0, max: 100, decimal: 1 }, required: false },
  { name: 'refLengthMax', type: 'number', range: { min: 0, max: 1000, decimal: 1 }, required: false },
  { name: 'refLengthMin', type: 'number', range: { min: 0, max: 1000, decimal: 1 }, required: false },
  { name: 'refHeightMax', type: 'number', range: { min: 0, max: 1000, decimal: 1 }, required: false },
  { name: 'refHpMax', type: 'number', range: { min: 0, max: 100, decimal: 1 }, required: false },
  { name: 'refHpMin', type: 'number', range: { min: 0, max: 100, decimal: 1 }, required: false },
  { name: 'refLpMax', type: 'number', range: { min: 0, max: 100, decimal: 1 }, required: false },
  { name: 'refLpMin', type: 'number', range: { min: 0, max: 100, decimal: 1 }, required: false },
  { name: 'refHpLpMax', type: 'number', range: { min: 0, max: 100, decimal: 1 }, required: false },
  { name: 'refHpLpMin', type: 'number', range: { min: 0, max: 100, decimal: 1 }, required: false },
  { name: 'heatingMin', type: 'number', range: { min: 0, max: 100, decimal: 1 }, required: false },
  { name: 'coolingMin', type: 'number', range: { min: 0, max: 100, decimal: 1 }, required: false },
  { name: 'ewMin', type: 'number', range: { min: 0, max: 10000, decimal: 1 }, required: false },
  { name: 'cwMin', type: 'number', range: { min: 0, max: 10000, decimal: 1 }, required: false },
  { name: 'heatingMaxLm', type: 'number', range: { min: 0, max: 100, decimal: 1 }, required: false },
  { name: 'heatingMinLm', type: 'number', range: { min: 0, max: 100, decimal: 1 }, required: false },
  { name: 'coolingMaxLm', type: 'number', range: { min: 0, max: 100, decimal: 1 }, required: false },
  { name: 'coolingMinLm', type: 'number', range: { min: 0, max: 100, decimal: 1 }, required: false },
  { name: 'ewMaxLm', type: 'number', range: { min: 0, max: 10000, decimal: 1 }, required: false },
  { name: 'ewMinLm', type: 'number', range: { min: 0, max: 10000, decimal: 1 }, required: false },
  { name: 'cwMaxLm', type: 'number', range: { min: 0, max: 10000, decimal: 1 }, required: false },
  { name: 'cwMinLm', type: 'number', range: { min: 0, max: 10000, decimal: 1 }, required: false },
  { name: 'htWaterPreMax', type: 'number', range: { min: 0, max: 10, decimal: 1 }, required: false },
  { name: 'htWaterPreMin', type: 'number', range: { min: 0, max: 10, decimal: 1 }, required: false },
  { name: 'dhWaterPreMax', type: 'number', range: { min: 0, max: 10, decimal: 1 }, required: false },
  { name: 'dhWaterPreMin', type: 'number', range: { min: 0, max: 10, decimal: 1 }, required: false },
  { name: 'ewWaterPreMax', type: 'number', range: { min: 0, max: 10, decimal: 1 }, required: false },
  { name: 'ewWaterPreMin', type: 'number', range: { min: 0, max: 10, decimal: 1 }, required: false },
  { name: 'cwWaterPreMax', type: 'number', range: { min: 0, max: 10, decimal: 1 }, required: false },
  { name: 'cwWaterPreMin', type: 'number', range: { min: 0, max: 10, decimal: 1 }, required: false },
  { name: 'htWaterTempMax', type: 'number', range: { min: -100, max: 100, decimal: 1 }, required: false },
  { name: 'htWaterTempMin', type: 'number', range: { min: -100, max: 100, decimal: 1 }, required: false },
  { name: 'dhWaterTempMax', type: 'number', range: { min: -100, max: 100, decimal: 1 }, required: false },
  { name: 'dhWaterTempMin', type: 'number', range: { min: -100, max: 100, decimal: 1 }, required: false },
  { name: 'ewWaterTempMax', type: 'number', range: { min: -100, max: 100, decimal: 1 }, required: false },
  { name: 'ewWaterTempMin', type: 'number', range: { min: -100, max: 100, decimal: 1 }, required: false },
  { name: 'cwWaterTempMax', type: 'number', range: { min: -100, max: 100, decimal: 1 }, required: false },
  { name: 'cwWaterTempMin', type: 'number', range: { min: -100, max: 100, decimal: 1 }, required: false },
  { name: 'glycolMax', type: 'number', range: { min: 0, max: 100, decimal: 0 }, required: false },
  { name: 'glycolMin', type: 'number', range: { min: 0, max: 100, decimal: 0 }, required: false },
  { name: 'glycolMaxDegC', type: 'number', range: { min: -100, max: 0, decimal: 0 }, required: false },
  { name: 'glycolMinDegC', type: 'number', range: { min: -100, max: 0, decimal: 0 }, required: false },
  { name: 'iuOuWaterDiameter', type: 'number', range: { min: 0, max: 100, decimal: 0 }, required: false },
  { name: 'shDiameter', type: 'number', range: { min: 0, max: 100, decimal: 0 }, required: false },
  { name: 'wsDiameter', type: 'number', range: { min: 0, max: 100, decimal: 0 }, required: false },
  { name: 'geoDiameter', type: 'number', range: { min: 0, max: 100, decimal: 0 }, required: false },
  { name: 'condWaterDiameter', type: 'number', range: { min: 0, max: 1000, decimal: 0 }, required: false },
  { name: 'evapWaterDiameter', type: 'number', range: { min: 0, max: 1000, decimal: 0 }, required: false },
  { name: 'drainDiameter', type: 'number', range: { min: 0, max: 100, decimal: 0 }, required: false },
  { name: 'drainHeightMax', type: 'number', range: { min: 0, max: 1000, decimal: 0 }, required: false },
  { name: 'supplyWaterDiameter', type: 'number', range: { min: 0, max: 100, decimal: 0 }, required: false },
  { name: 'magneticFilterDirtSeparator', type: 'bool', required: false },
  { name: 'autoAirpurge', type: 'bool', required: false },
  { name: 'manualAirpurge', type: 'bool', required: false },
  { name: 'unitPowerMax', type: 'number', range: { min: 0, max: 100000, decimal: 0 }, required: false },
  { name: 'unitPowerMin', type: 'number', range: { min: 0, max: 100000, decimal: 0 }, required: false },
  { name: 'unitCurrentMax', type: 'number', range: { min: 0, max: 1000, decimal: 1 }, required: false },
  { name: 'unitFuseRate', type: 'number', range: { min: 0, max: 1000, decimal: 0 }, required: false },
  { name: 'unitPhase', type: 'number', range: { min: 1, max: 3, decimal: 0 }, required: false },
  { name: 'unitFrequency', type: 'number', range: { min: 50, max: 60, decimal: 0 }, required: false },
  { name: 'unitPowerCableDiameter', type: 'number', range: { min: 0, max: 100, decimal: 1 }, required: false },
  { name: 'iuOuCableDiameter', type: 'number', range: { min: 0, max: 100, decimal: 1 }, required: false },
  { name: 'unitPowerCableArea', type: 'number', range: { min: 0, max: 100, decimal: 1 }, required: false },
  { name: 'iuOuCableArea', type: 'number', range: { min: 0, max: 100, decimal: 1 }, required: false },
  { name: 'iuOuCableLengthMax', type: 'number', range: { min: 0, max: 100, decimal: 0 }, required: false },
  { name: 'compType', type: 'string', required: false },
  { name: 'compModel1', type: 'string', required: false },
  { name: 'compModel2', type: 'string', required: false },
  { name: 'compQty', type: 'number', range: { min: 0, max: 10, decimal: 0 }, required: false },
  { name: 'compMax', type: 'number', range: { min: 0, max: 100000, decimal: 0 }, required: false },
  { name: 'compMin', type: 'number', range: { min: 0, max: 100000, decimal: 0 }, required: false },
  { name: 'comp1Max', type: 'number', range: { min: 0, max: 1000, decimal: 1 }, required: false },
  { name: 'comp1Min', type: 'number', range: { min: 0, max: 1000, decimal: 1 }, required: false },
  { name: 'compMaxA', type: 'number', range: { min: 0, max: 1000, decimal: 1 }, required: false },
  { name: 'comp2Min', type: 'number', range: { min: 0, max: 1000, decimal: 1 }, required: false },
  { name: 'compStepsMax', type: 'number', range: { min: 0, max: 1000, decimal: 0 }, required: false },
  { name: 'compStepsMin', type: 'number', range: { min: 0, max: 1000, decimal: 0 }, required: false },
  { name: 'compMaxHz', type: 'number', range: { min: 0, max: 1000, decimal: 0 }, required: false },
  { name: 'compMinHz', type: 'number', range: { min: 0, max: 1000, decimal: 0 }, required: false },
  { name: 'compHeaterMax', type: 'number', range: { min: 0, max: 1000, decimal: 0 }, required: false },
  { name: 'compHeaterMin', type: 'number', range: { min: 0, max: 1000, decimal: 0 }, required: false },
  { name: 'compHeaterMaxA', type: 'number', range: { min: 0, max: 1000, decimal: 0 }, required: false },
  { name: 'compHeaterMinA', type: 'number', range: { min: 0, max: 1000, decimal: 0 }, required: false },
  { name: 'compHeaterOn', type: 'number', range: { min: 0, max: 100, decimal: 0 }, required: false },
  { name: 'compHeaterOff', type: 'number', range: { min: 0, max: 100, decimal: 0 }, required: false },
  { name: 'compHeaterMaxKOhm', type: 'number', range: { min: 0, max: 100, decimal: 1 }, required: false },
  { name: 'compHeaterMinKOhm', type: 'number', range: { min: 0, max: 100, decimal: 1 }, required: false },
  { name: 'compDischargeMax', type: 'number', range: { min: 0, max: 1000, decimal: 1 }, required: false },
  { name: 'compDischargeMin', type: 'number', range: { min: 0, max: 1000, decimal: 1 }, required: false },
  { name: 'compSuctionMax', type: 'number', range: { min: -100, max: 100, decimal: 1 }, required: false },
  { name: 'compSuctionMin', type: 'number', range: { min: -100, max: 100, decimal: 1 }, required: false },
  { name: 'compBodyMax', type: 'number', range: { min: 0, max: 1000, decimal: 1 }, required: false },
  { name: 'compBodyMin', type: 'number', range: { min: 0, max: 1000, decimal: 1 }, required: false },
  { name: 'compDshMax', type: 'number', range: { min: 0, max: 100, decimal: 1 }, required: false },
  { name: 'compSshMin', type: 'number', range: { min: 0, max: 100, decimal: 1 }, required: false },
  { name: 'compSshMax', type: 'number', range: { min: 0, max: 100, decimal: 1 }, required: false },
  { name: 'compDshMin', type: 'number', range: { min: 0, max: 100, decimal: 1 }, required: false },
  { name: 'compUVMax', type: 'number', range: { min: 0, max: 10, decimal: 3 }, required: false },
  { name: 'compUVMin', type: 'number', range: { min: 0, max: 10, decimal: 3 }, required: false },
  { name: 'compVWMax', type: 'number', range: { min: 0, max: 10, decimal: 3 }, required: false },
  { name: 'compVWMin', type: 'number', range: { min: 0, max: 10, decimal: 3 }, required: false },
  { name: 'compUWMax', type: 'number', range: { min: 0, max: 10, decimal: 3 }, required: false },
  { name: 'compUWMin', type: 'number', range: { min: 0, max: 10, decimal: 3 }, required: false },
  { name: 'fanDc', type: 'bool', required: false },
  { name: 'fanQty', type: 'number', range: { min: 0, max: 100, decimal: 0 }, required: false },
  { name: 'fanAirFlowMax', type: 'number', range: { min: 0, max: 10000, decimal: 0 }, required: false },
  { name: 'fanAirFlowMin', type: 'number', range: { min: 0, max: 10000, decimal: 0 }, required: false },
  { name: 'fanMax', type: 'number', range: { min: 0, max: 1000, decimal: 0 }, required: false },
  { name: 'fanMin', type: 'number', range: { min: 0, max: 1000, decimal: 0 }, required: false },
  { name: 'fanMaxA', type: 'number', range: { min: 0, max: 1000, decimal: 0 }, required: false },
  { name: 'fanMinA', type: 'number', range: { min: 0, max: 1000, decimal: 0 }, required: false },
  { name: 'fanMaxOhm', type: 'number', range: { min: 0, max: 10, decimal: 2 }, required: false },
  { name: 'fanMinOhm', type: 'number', range: { min: 0, max: 10, decimal: 2 }, required: false },
  { name: 'fanControlMax', type: 'number', range: { min: 0, max: 1000, decimal: 1 }, required: false },
  { name: 'fanControlMin', type: 'number', range: { min: 0, max: 1000, decimal: 1 }, required: false },
  { name: 'fanRotationMax', type: 'number', range: { min: 0, max: 1000, decimal: 1 }, required: false },
  { name: 'fanRotationMin', type: 'number', range: { min: 0, max: 1000, decimal: 1 }, required: false },
  { name: 'fanStepsMax', type: 'number', range: { min: 0, max: 1000, decimal: 0 }, required: false },
  { name: 'fanStepsMin', type: 'number', range: { min: 0, max: 1000, decimal: 0 }, required: false },
  { name: 'fanVfd', type: 'bool', required: false },
  { name: 'fanMaxHz', type: 'number', range: { min: 0, max: 1000, decimal: 0 }, required: false },
  { name: 'fanMinHz', type: 'number', range: { min: 0, max: 1000, decimal: 0 }, required: false },
  { name: 'condWaterCooled', type: 'bool', required: false },
  { name: 'condPressurDrop', type: 'number', range: { min: 0, max: 1000, decimal: 1 }, required: false },
  { name: 'condApproachMax', type: 'number', range: { min: 0, max: 100, decimal: 1 }, required: false },
  { name: 'condApproachMin', type: 'number', range: { min: 0, max: 100, decimal: 1 }, required: false },
  { name: 'condScMax', type: 'number', range: { min: 0, max: 100, decimal: 1 }, required: false },
  { name: 'condScMin', type: 'number', range: { min: 0, max: 100, decimal: 1 }, required: false },
  { name: 'evapPressurDrop', type: 'number', range: { min: 0, max: 1000, decimal: 1 }, required: false },
  { name: 'evapApproachMax', type: 'number', range: { min: 0, max: 100, decimal: 1 }, required: false },
  { name: 'evapApproachMin', type: 'number', range: { min: 0, max: 100, decimal: 1 }, required: false },
  { name: 'gearEexv', type: 'bool', required: false },
  { name: 'exvLiquidInMax', type: 'number', range: { min: 0, max: 100, decimal: 1 }, required: false },
  { name: 'exvLiquidInMin', type: 'number', range: { min: 0, max: 100, decimal: 1 }, required: false },
  { name: 'exvLiquidOutMax', type: 'number', range: { min: 0, max: 100, decimal: 1 }, required: false },
  { name: 'exvLiquidOutMin', type: 'number', range: { min: 0, max: 100, decimal: 1 }, required: false },
  { name: 'eexvMainMax', type: 'number', range: { min: 0, max: 100, decimal: 0 }, required: false },
  { name: 'eexvMainMin', type: 'number', range: { min: 0, max: 100, decimal: 0 }, required: false },
  { name: 'eexvInjeMax', type: 'number', range: { min: 0, max: 100, decimal: 0 }, required: false },
  { name: 'eexvInjeMin', type: 'number', range: { min: 0, max: 100, decimal: 0 }, required: false },
  { name: 'eexvPulseMax', type: 'number', range: { min: 0, max: 10000, decimal: 0 }, required: false },
  { name: 'eexvPulseMin', type: 'number', range: { min: 0, max: 10000, decimal: 0 }, required: false },
  { name: 'eexvMax', type: 'number', range: { min: 0, max: 100, decimal: 0 }, required: false },
  { name: 'eexvMin', type: 'number', range: { min: 0, max: 100, decimal: 0 }, required: false },
  { name: 'ecoLiquidInMax', type: 'number', range: { min: 0, max: 100, decimal: 1 }, required: false },
  { name: 'ecoLiquidInMin', type: 'number', range: { min: 0, max: 100, decimal: 1 }, required: false },
  { name: 'ecoLiquidOutMax', type: 'number', range: { min: 0, max: 100, decimal: 1 }, required: false },
  { name: 'ecoLiquidOutMin', type: 'number', range: { min: 0, max: 100, decimal: 1 }, required: false },
  { name: 'ecoInjeInMax', type: 'number', range: { min: 0, max: 100, decimal: 1 }, required: false },
  { name: 'ecoInjeInMin', type: 'number', range: { min: 0, max: 100, decimal: 1 }, required: false },
  { name: 'ecoInjeOutMax', type: 'number', range: { min: 0, max: 100, decimal: 1 }, required: false },
  { name: 'ecoInjeOutMin', type: 'number', range: { min: 0, max: 100, decimal: 1 }, required: false },
  { name: 'contType', type: 'string', required: false },
  { name: 'contVoltageMax', type: 'number', range: { min: 0, max: 1000, decimal: 0 }, required: false },
  { name: 'contVoltageMin', type: 'number', range: { min: 0, max: 1000, decimal: 0 }, required: false },
  { name: 'typeElConn', type: 'bool', required: false },
  { name: 'contCableType', type: 'string', required: false },
  { name: 'contCableDiameter', type: 'number', range: { min: 0, max: 100, decimal: 0 }, required: false },
  { name: 'contCableLengthMax', type: 'number', range: { min: 0, max: 10000, decimal: 0 }, required: false },
  { name: 'mainPumpMax', type: 'number', range: { min: 0, max: 1000, decimal: 0 }, required: false },
  { name: 'mainPumpMin', type: 'number', range: { min: 0, max: 1000, decimal: 0 }, required: false },
  { name: 'mainPumpMaxA', type: 'number', range: { min: 0, max: 1000, decimal: 1 }, required: false },
  { name: 'mainPumpMinA', type: 'number', range: { min: 0, max: 1000, decimal: 1 }, required: false },
  { name: 'mainPumpPwmMax', type: 'number', range: { min: 0, max: 100, decimal: 0 }, required: false },
  { name: 'mainPumpPwmMin', type: 'number', range: { min: 0, max: 100, decimal: 0 }, required: false },
  { name: 'mainPumpMaxHz', type: 'number', range: { min: 0, max: 1000, decimal: 0 }, required: false },
  { name: 'mainPumpMinHz', type: 'number', range: { min: 0, max: 1000, decimal: 0 }, required: false },
  { name: 'bizonePumpMax', type: 'number', range: { min: 0, max: 1000, decimal: 0 }, required: false },
  { name: 'bizonePumpMin', type: 'number', range: { min: 0, max: 1000, decimal: 0 }, required: false },
  { name: 'bizonePumpMaxA', type: 'number', range: { min: 0, max: 1000, decimal: 1 }, required: false },
  { name: 'bizonePumpMinA', type: 'number', range: { min: 0, max: 1000, decimal: 1 }, required: false },
  { name: 'bizonePumpPwmMax', type: 'number', range: { min: 0, max: 100, decimal: 0 }, required: false },
  { name: 'bizonePumpPwmMin', type: 'number', range: { min: 0, max: 100, decimal: 0 }, required: false },
  { name: 'bizonePumpMaxHz', type: 'number', range: { min: 0, max: 1000, decimal: 0 }, required: false },
  { name: 'bizonePumpMinHz', type: 'number', range: { min: 0, max: 1000, decimal: 0 }, required: false },
  { name: 'threeWvVoltageMax', type: 'number', range: { min: 0, max: 1000, decimal: 0 }, required: false },
  { name: 'threeWvVoltageMin', type: 'number', range: { min: 0, max: 1000, decimal: 0 }, required: false },
  { name: 'threeWvMax', type: 'number', range: { min: 0, max: 100, decimal: 1 }, required: false },
  { name: 'threeWvMin', type: 'number', range: { min: 0, max: 100, decimal: 1 }, required: false },
  { name: 'threeWvBizone', type: 'bool', required: false },
  { name: 'threeWvBizoneMax', type: 'number', range: { min: 0, max: 1000, decimal: 0 }, required: false },
  { name: 'threeWvBizoneMin', type: 'number', range: { min: 0, max: 1000, decimal: 0 }, required: false },
  { name: 'threeWvBizoneMaxKOhm', type: 'number', range: { min: 0, max: 100, decimal: 1 }, required: false },
  { name: 'threeWvBizoneMinKOhm', type: 'number', range: { min: 0, max: 100, decimal: 1 }, required: false },
  { name: 'threeWvGeneratorMax', type: 'number', range: { min: 0, max: 1000, decimal: 0 }, required: false },
  { name: 'threeWvGeneratorMin', type: 'number', range: { min: 0, max: 1000, decimal: 0 }, required: false },
  { name: 'threeWvGeneratorMaxKOhm', type: 'number', range: { min: 0, max: 100, decimal: 1 }, required: false },
  { name: 'threeWvGeneratorMinKOhm', type: 'number', range: { min: 0, max: 100, decimal: 1 }, required: false },
  { name: 'main4wvMax', type: 'number', range: { min: 0, max: 1000, decimal: 0 }, required: false },
  { name: 'main4wvMin', type: 'number', range: { min: 0, max: 1000, decimal: 0 }, required: false },
  { name: 'main4wvMaxKOhm', type: 'number', range: { min: 0, max: 1000, decimal: 1 }, required: false },
  { name: 'main4wvMinKOhm', type: 'number', range: { min: 0, max: 1000, decimal: 1 }, required: false },
  { name: 'sub4wvMax', type: 'number', range: { min: 0, max: 1000, decimal: 0 }, required: false },
  { name: 'sub4wvMin', type: 'number', range: { min: 0, max: 1000, decimal: 0 }, required: false },
  { name: 'sub4wvMaxKOhm', type: 'number', range: { min: 0, max: 1000, decimal: 1 }, required: false },
  { name: 'sub4wvMinKOhm', type: 'number', range: { min: 0, max: 1000, decimal: 1 }, required: false },
  { name: 'mainPcbPowerMax', type: 'number', range: { min: 0, max: 1000, decimal: 0 }, required: false },
  { name: 'mainPcbPowerMin', type: 'number', range: { min: 0, max: 1000, decimal: 0 }, required: false },
  { name: 'mainPcbFanMax', type: 'number', range: { min: 0, max: 1000, decimal: 0 }, required: false },
  { name: 'mainPcbFanMin', type: 'number', range: { min: 0, max: 1000, decimal: 0 }, required: false },
  { name: 'mainPcbRectifierMax', type: 'number', range: { min: 0, max: 1000, decimal: 0 }, required: false },
  { name: 'mainPcbRectifierMin', type: 'number', range: { min: 0, max: 1000, decimal: 0 }, required: false },
  { name: 'mainPcbV1rMax', type: 'number', range: { min: 0, max: 1, decimal: 3 }, required: false },
  { name: 'mainPcbV1rMin', type: 'number', range: { min: 0, max: 1, decimal: 3 }, required: false },
  { name: 'mainPcbV2rMax', type: 'number', range: { min: 0, max: 1, decimal: 3 }, required: false },
  { name: 'mainPcbV2rMin', type: 'number', range: { min: 0, max: 1, decimal: 3 }, required: false },
  { name: 'mainPcbV3rMax', type: 'number', range: { min: 0, max: 1, decimal: 3 }, required: false },
  { name: 'mainPcbV3rMin', type: 'number', range: { min: 0, max: 1, decimal: 3 }, required: false },
  { name: 'mainPcbV4rMax', type: 'number', range: { min: 0, max: 1, decimal: 3 }, required: false },
  { name: 'mainPcbV4rMin', type: 'number', range: { min: 0, max: 1, decimal: 3 }, required: false },
  { name: 'noisePcbPowerMax', type: 'number', range: { min: 0, max: 1000, decimal: 0 }, required: false },
  { name: 'noisePcbPowerMin', type: 'number', range: { min: 0, max: 1000, decimal: 0 }, required: false },
  { name: 'noisePcbLaMax', type: 'number', range: { min: 0, max: 1000, decimal: 0 }, required: false },
  { name: 'noisePcbLaMin', type: 'number', range: { min: 0, max: 1000, decimal: 0 }, required: false },
  { name: 'noisePcbLbMax', type: 'number', range: { min: 0, max: 1000, decimal: 0 }, required: false },
  { name: 'noisePcbLbMin', type: 'number', range: { min: 0, max: 1000, decimal: 0 }, required: false },
  { name: 'acspcbMax', type: 'number', range: { min: 0, max: 1000, decimal: 0 }, required: false },
  { name: 'acspcbMin', type: 'number', range: { min: 0, max: 1000, decimal: 0 }, required: false },
  { name: 'bizonePcbMax', type: 'number', range: { min: 0, max: 1000, decimal: 0 }, required: false },
  { name: 'bizonePcbMin', type: 'number', range: { min: 0, max: 1000, decimal: 0 }, required: false },
  { name: 'currentLoopPcbMax', type: 'number', range: { min: 0, max: 1000, decimal: 0 }, required: false },
  { name: 'currentLoopPcbMin', type: 'number', range: { min: 0, max: 1000, decimal: 0 }, required: false },
  { name: 'flashPcbMax', type: 'number', range: { min: 0, max: 1000, decimal: 0 }, required: false },
  { name: 'flashPcbMin', type: 'number', range: { min: 0, max: 1000, decimal: 0 }, required: false },
  { name: 'hydroPcbMax', type: 'number', range: { min: 0, max: 1000, decimal: 0 }, required: false },
  { name: 'hydroPcbMin', type: 'number', range: { min: 0, max: 1000, decimal: 0 }, required: false },
  { name: 'hydroExtPcbMax', type: 'number', range: { min: 0, max: 1000, decimal: 0 }, required: false },
  { name: 'hydroExtPcbMin', type: 'number', range: { min: 0, max: 1000, decimal: 0 }, required: false },
  { name: 'invpcbLaMax', type: 'number', range: { min: 0, max: 1000, decimal: 0 }, required: false },
  { name: 'invpcbLaMin', type: 'number', range: { min: 0, max: 1000, decimal: 0 }, required: false },
  { name: 'invpcbLbMax', type: 'number', range: { min: 0, max: 1000, decimal: 0 }, required: false },
  { name: 'invpcbLbMin', type: 'number', range: { min: 0, max: 1000, decimal: 0 }, required: false },
  { name: 'interfacePcbMax', type: 'number', range: { min: 0, max: 1000, decimal: 1 }, required: false },
  { name: 'interfacePcbMin', type: 'number', range: { min: 0, max: 1000, decimal: 1 }, required: false },
  { name: 'leakPcbMax', type: 'number', range: { min: 0, max: 1000, decimal: 0 }, required: false },
  { name: 'leakPcbMin', type: 'number', range: { min: 0, max: 1000, decimal: 0 }, required: false },
  { name: 'leakPcbMaxOhm', type: 'number', range: { min: 0, max: 100, decimal: 0 }, required: false },
  { name: 'leakPcbMinOhm', type: 'number', range: { min: 0, max: 100, decimal: 0 }, required: false },
  { name: 'bhupcbControlMax', type: 'number', range: { min: 0, max: 1000, decimal: 0 }, required: false },
  { name: 'bhupcbControlMin', type: 'number', range: { min: 0, max: 1000, decimal: 0 }, required: false },
  { name: 'bhupcbPowerMax', type: 'number', range: { min: 0, max: 1000, decimal: 0 }, required: false },
  { name: 'bhupcbPowerMin', type: 'number', range: { min: 0, max: 1000, decimal: 0 }, required: false },
  { name: 'powerSupplyPcbMax', type: 'number', range: { min: 0, max: 1000, decimal: 0 }, required: false },
  { name: 'powerSupplyPcbMin', type: 'number', range: { min: 0, max: 1000, decimal: 0 }, required: false },
  { name: 'powerSupplyPcbPcbMax', type: 'number', range: { min: 0, max: 1000, decimal: 0 }, required: false },
  { name: 'powerSupplyPcbPcbMin', type: 'number', range: { min: 0, max: 1000, decimal: 0 }, required: false },
  { name: 'reactorMax', type: 'number', range: { min: 0, max: 100, decimal: 1 }, required: false },
  { name: 'reactorMin', type: 'number', range: { min: 0, max: 100, decimal: 1 }, required: false },
  { name: 'buhType', type: 'string', required: false },
  { name: 'buh3v', type: 'bool', required: false },
  { name: 'buh3vFuseRate', type: 'number', range: { min: 0, max: 100, decimal: 0 }, required: false },
  { name: 'buh3vMax', type: 'number', range: { min: 0, max: 1000, decimal: 0 }, required: false },
  { name: 'buh3vMin', type: 'number', range: { min: 0, max: 1000, decimal: 0 }, required: false },
  { name: 'buh6v1n', type: 'bool', required: false },
  { name: 'buh6v1nFuseRate', type: 'number', range: { min: 0, max: 100, decimal: 0 }, required: false },
  { name: 'buh6v1nMax', type: 'number', range: { min: 0, max: 1000, decimal: 0 }, required: false },
  { name: 'buh6v1nMin', type: 'number', range: { min: 0, max: 1000, decimal: 0 }, required: false },
  { name: 'buh6v3n', type: 'bool', required: false },
  { name: 'buh6v3nFuseRate', type: 'number', range: { min: 0, max: 100, decimal: 0 }, required: false },
  { name: 'buh6v3nMax', type: 'number', range: { min: 0, max: 1000, decimal: 0 }, required: false },
  { name: 'buh6v3nMin', type: 'number', range: { min: 0, max: 1000, decimal: 0 }, required: false },
  { name: 'buh9w', type: 'bool', required: false },
  { name: 'buh9wFuseRate', type: 'number', range: { min: 0, max: 100, decimal: 0 }, required: false },
  { name: 'buh9wMax', type: 'number', range: { min: 0, max: 1000, decimal: 0 }, required: false },
  { name: 'buh9wMin', type: 'number', range: { min: 0, max: 1000, decimal: 0 }, required: false },
  { name: 'bshMax', type: 'number', range: { min: 0, max: 1000, decimal: 0 }, required: false },
  { name: 'bshMin', type: 'number', range: { min: 0, max: 1000, decimal: 0 }, required: false },
  { name: 'bshMaxOhm', type: 'number', range: { min: 0, max: 100, decimal: 2 }, required: false },
  { name: 'bshMinOhm', type: 'number', range: { min: 0, max: 100, decimal: 2 }, required: false },
  { name: 'reliefValve', type: 'number', range: { min: 0, max: 10000, decimal: 1 }, required: false },
  { name: 'reliefValveDegC', type: 'number', range: { min: 0, max: 1000, decimal: 1 }, required: false },
  { name: 'antiFreezeValve', type: 'number', range: { min: -10, max: 10, decimal: 1 }, required: false },
  { name: 'buhThermalProtector', type: 'number', range: { min: 0, max: 100, decimal: 1 }, required: false },
  { name: 'bshThermalProtector', type: 'number', range: { min: 0, max: 100, decimal: 1 }, required: false },
  { name: 'hps1OperatingMax', type: 'number', range: { min: 0, max: 10, decimal: 2 }, required: false },
  { name: 'hps1OperatingMin', type: 'number', range: { min: 0, max: 10, decimal: 2 }, required: false },
  { name: 'hps1ResetMax', type: 'number', range: { min: 0, max: 10, decimal: 2 }, required: false },
  { name: 'hps1ResetMin', type: 'number', range: { min: 0, max: 10, decimal: 2 }, required: false },
  { name: 'hps1VoltageMax', type: 'number', range: { min: 0, max: 1000, decimal: 1 }, required: false },
  { name: 'hps1VoltageMin', type: 'number', range: { min: 0, max: 1000, decimal: 1 }, required: false },
  { name: 'hps2OperatingMax', type: 'number', range: { min: 0, max: 10, decimal: 2 }, required: false },
  { name: 'hps2OperatingMin', type: 'number', range: { min: 0, max: 10, decimal: 2 }, required: false },
  { name: 'hps2ResetMax', type: 'number', range: { min: 0, max: 10, decimal: 2 }, required: false },
  { name: 'hps2ResetMin', type: 'number', range: { min: 0, max: 10, decimal: 2 }, required: false },
  { name: 'hps2VoltageMax', type: 'number', range: { min: 0, max: 1000, decimal: 1 }, required: false },
  { name: 'hps2VoltageMin', type: 'number', range: { min: 0, max: 1000, decimal: 1 }, required: false },
  { name: 'lps1OperatingMax', type: 'number', range: { min: -10, max: 10, decimal: 2 }, required: false },
  { name: 'lps1OperatingMin', type: 'number', range: { min: -10, max: 10, decimal: 2 }, required: false },
  { name: 'lps1ResetMax', type: 'number', range: { min: 0, max: 10, decimal: 2 }, required: false },
  { name: 'lps1ResetMin', type: 'number', range: { min: 0, max: 10, decimal: 2 }, required: false },
  { name: 'lps1VoltageMax', type: 'number', range: { min: 0, max: 1000, decimal: 1 }, required: false },
  { name: 'lps1VoltageMin', type: 'number', range: { min: 0, max: 1000, decimal: 1 }, required: false },
  { name: 'lps2OperatingMax', type: 'number', range: { min: -10, max: 10, decimal: 2 }, required: false },
  { name: 'lps2OperatingMin', type: 'number', range: { min: -10, max: 10, decimal: 2 }, required: false },
  { name: 'lps2ResetMax', type: 'number', range: { min: 0, max: 10, decimal: 2 }, required: false },
  { name: 'lps2ResetMin', type: 'number', range: { min: 0, max: 10, decimal: 2 }, required: false },
  { name: 'lps2VoltageMax', type: 'number', range: { min: 0, max: 1000, decimal: 1 }, required: false },
  { name: 'lps2VoltageMin', type: 'number', range: { min: 0, max: 1000, decimal: 1 }, required: false },
  { name: 'lps2ConnectedTo', type: 'string', required: false },
  { name: 'lps2IntermediateConnect', type: 'bool', required: false },
  { name: 'hpSensorMax', type: 'number', range: { min: 0, max: 10, decimal: 1 }, required: false },
  { name: 'hpSensorMin', type: 'number', range: { min: 0, max: 10, decimal: 1 }, required: false },
  { name: 'hpSensorReadMax', type: 'number', range: { min: 0, max: 10, decimal: 1 }, required: false },
  { name: 'hpSensorReadMin', type: 'number', range: { min: 0, max: 10, decimal: 1 }, required: false },
  { name: 'lpSensorMax', type: 'number', range: { min: 0, max: 10, decimal: 1 }, required: false },
  { name: 'lpSensorMin', type: 'number', range: { min: 0, max: 10, decimal: 1 }, required: false },
  { name: 'lpSensorReadMax', type: 'number', range: { min: 0, max: 10, decimal: 1 }, required: false },
  { name: 'lpSensorReadMin', type: 'number', range: { min: 0, max: 10, decimal: 1 }, required: false },
  { name: 'wpSensorMax', type: 'number', range: { min: 0, max: 10, decimal: 1 }, required: false },
  { name: 'wpSensorMin', type: 'number', range: { min: 0, max: 10, decimal: 1 }, required: false },
  { name: 'wpSensorReadMax', type: 'number', range: { min: 0, max: 10, decimal: 1 }, required: false },
  { name: 'wpSensorReadMin', type: 'number', range: { min: 0, max: 10, decimal: 1 }, required: false },
  { name: 'svMax', type: 'number', range: { min: 0, max: 1000, decimal: 0 }, required: false },
  { name: 'svMin', type: 'number', range: { min: 0, max: 1000, decimal: 0 }, required: false },
  { name: 'svMaxKOhm', type: 'number', range: { min: 0, max: 10, decimal: 1 }, required: false },
  { name: 'svMinKOhm', type: 'number', range: { min: 0, max: 10, decimal: 1 }, required: false },
  { name: 'refThermistorAMax', type: 'number', range: { min: 0, max: 1000, decimal: 2 }, required: false },
  { name: 'refThermistorAMin', type: 'number', range: { min: 0, max: 1000, decimal: 2 }, required: false },
  { name: 'refThermistorBMax', type: 'number', range: { min: 0, max: 1000, decimal: 2 }, required: false },
  { name: 'refThermistorBMin', type: 'number', range: { min: 0, max: 1000, decimal: 2 }, required: false },
  { name: 'refThermistorCMax', type: 'number', range: { min: 0, max: 1000, decimal: 2 }, required: false },
  { name: 'refThermistorCMin', type: 'number', range: { min: 0, max: 1000, decimal: 2 }, required: false },
  { name: 'waterThermistorAMax', type: 'number', range: { min: 0, max: 1000, decimal: 2 }, required: false },
  { name: 'waterThermistorAMin', type: 'number', range: { min: 0, max: 1000, decimal: 2 }, required: false },
  { name: 'waterThermistorBMax', type: 'number', range: { min: 0, max: 1000, decimal: 2 }, required: false },
  { name: 'waterThermistorBMin', type: 'number', range: { min: 0, max: 1000, decimal: 2 }, required: false },
  { name: 'waterThermistorCMax', type: 'number', range: { min: 0, max: 1000, decimal: 2 }, required: false },
  { name: 'waterThermistorCMin', type: 'number', range: { min: 0, max: 1000, decimal: 2 }, required: false },
  { name: 'flowSensorMax', type: 'number', range: { min: 0, max: 10, decimal: 1 }, required: false },
  { name: 'flowSensorMin', type: 'number', range: { min: 0, max: 10, decimal: 1 }, required: false },
  { name: 'flowSensorMaxHz', type: 'number', range: { min: 0, max: 1000, decimal: 0 }, required: false },
  { name: 'flowSensorMinHz', type: 'number', range: { min: 0, max: 1000, decimal: 0 }, required: false },
  { name: 'gasSensorIuMax', type: 'number', range: { min: 0, max: 10, decimal: 1 }, required: false },
  { name: 'gasSensorIuMix', type: 'number', range: { min: 0, max: 10, decimal: 1 }, required: false },
  { name: 'gasSensorIuMaxPct', type: 'number', range: { min: 0, max: 100, decimal: 0 }, required: false },
  { name: 'gasSensorIuMixPct', type: 'number', range: { min: 0, max: 100, decimal: 0 }, required: false },
  { name: 'gasSensorOuMax', type: 'number', range: { min: 0, max: 10, decimal: 1 }, required: false },
  { name: 'gasSensorOuMix', type: 'number', range: { min: 0, max: 10, decimal: 1 }, required: false },
  { name: 'gasSensorOuMaxPct', type: 'number', range: { min: 0, max: 100, decimal: 0 }, required: false },
  { name: 'gasSensorOuMixPct', type: 'number', range: { min: 0, max: 100, decimal: 0 }, required: false },
  { name: 'refrigerantRectifier', type: 'bool', required: false },
  { name: 'refrigerantRectifierMax', type: 'number', range: { min: 0, max: 10, decimal: 1 }, required: false },
  { name: 'refrigerantRectifierMin', type: 'number', range: { min: 0, max: 10, decimal: 1 }, required: false },
  { name: 'ekhvconvReversibleKitInstalled', type: 'bool', required: false },
  { name: 'boilerDaikin', type: 'bool', required: false },
  { name: 'boilerVentDiameter', type: 'number', range: { min: 0, max: 100, decimal: 0 }, required: false },
  { name: 'boilerFlueDiameter', type: 'number', range: { min: 0, max: 100, decimal: 0 }, required: false },
  { name: 'boilerVentLengthMax', type: 'number', range: { min: 0, max: 100, decimal: 0 }, required: false },
  { name: 'boilerFlueLengthMax', type: 'number', range: { min: 0, max: 100, decimal: 0 }, required: false },
  { name: 'boilerFlueVentMin', type: 'number', range: { min: 0, max: 100, decimal: 0 }, required: false },
  { name: 'boilerGasPressier', type: 'number', range: { min: 0, max: 100, decimal: 0 }, required: false },
  { name: 'boilerCo2HMax', type: 'number', range: { min: 0, max: 100, decimal: 1 }, required: false },
  { name: 'boilerCo2HMin', type: 'number', range: { min: 0, max: 100, decimal: 1 }, required: false },
  { name: 'boilerCo2LMax', type: 'number', range: { min: 0, max: 100, decimal: 1 }, required: false },
  { name: 'boilerCo2LMin', type: 'number', range: { min: 0, max: 100, decimal: 1 }, required: false },
  { name: 'boilerMax', type: 'number', range: { min: 0, max: 100, decimal: 1 }, required: false },
  { name: 'boilerMin', type: 'number', range: { min: 0, max: 100, decimal: 1 }, required: false },
];
